import { useState, useEffect } from 'react';
import { 
  Users, AlertTriangle, TrendingUp, TrendingDown, FileText, Search, Filter, 
  Activity, Heart, Droplet, ThermometerSun, Weight, Stethoscope, CheckCircle,
  XCircle, Clock, Eye, Calendar, ArrowLeft, Download, AlertCircle, MessageSquare,
  BarChart3, LineChart as LineChartIcon, Video, MessageCircle, Pill, FlaskConical,
  Footprints, UserPlus, TestTube, Microscope, User, Mail, Phone, MapPin, Edit2,
  Home, Check, X,
  Plus,
  Pencil,
  Trash2
} from 'lucide-react';
import { format } from 'date-fns';
import { SixMWTReportView } from './SixMWTReportView';
import { DoctorLowAdherenceDetail } from './doctor/DoctorLowAdherenceDetail';
import { ClinicalSummaryTab } from './ClinicalSummaryTab';
import { LabResultsTrends } from './doctor/LabResultsTrends';
import { DrugMonitoring } from './doctor/DrugMonitoring';
import { FEV1TrendsCard } from './doctor/FEV1TrendsCard';
import { SixMWDTrendsCard } from './doctor/SixMWDTrendsCard';
import { DoctorSymptomsReview } from './doctor/DoctorSymptomsReview';
import { HomeNurseTasksView } from './doctor/HomeNurseTasksView';
import { HomeNurseTasksViewCompact } from './doctor/HomeNurseTasksViewCompact';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Separator } from './ui/separator';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, ReferenceLine, Area, AreaChart, Legend } from 'recharts';
import { toast } from 'sonner';
import { DischargeSummaryOnboarding } from './shared/DischargeSummaryOnboarding';
import { useDoctorDashboard } from '../lib/hooks/useDashboards';
import { useAuth } from '../lib/hooks/useAuth';
import { useFEV1Trends, useSixMWTTrends, useVitalsTrends } from '../lib/hooks/useTrends';
import { useSixMWTHistory } from '../lib/hooks/useTests';
import { useDischargeSummaries } from '../lib/hooks/useDischargeSummaries';
import { usePatientAdherenceDetails, useComprehensiveAdherenceDetails } from '../lib/hooks/useAdherence';
import { useLabResults } from '../lib/hooks/useLabResults';
import { useSymptomHistory } from '../lib/hooks/useSymptoms';
import { useMedications } from '../lib/hooks/useMedications';
import { useClinicalNotes, useCreateClinicalNote, useUpdateClinicalNote, useDeleteClinicalNote } from '../lib/hooks/useClinicalNotes';
import { LoadingState } from './common/LoadingState';
import { AlertCard } from './common/AlertCard';
import { EmptyState } from './common/EmptyState';
import { VideoCallScreen } from './shared/VideoCallScreen';
import { medicationsApi } from '@/lib/api';

interface DoctorDashboardProps {
  onNavigate?: (view: 'dashboard' | 'exercise' | 'vitals' | 'reports' | 'messaging' | 'appointments' | 'library' | 'settings' | 'notifications') => void;
}

// Medications state
interface Medication {
  id: string;
  name: string;
  dosage: string;
  frequency: string;
  scheduled_times: string;
  notes?: string;
}

// Wrapper component to fetch comprehensive adherence data
interface AdherenceDetailWrapperProps {
  patientId: number;
  onBack: () => void;
  onMarkReviewed: (patientId: string, notes: string) => void;
}

function AdherenceDetailWrapper({ patientId, onBack, onMarkReviewed }: AdherenceDetailWrapperProps) {
  const { data: comprehensiveData, isLoading, error } = useComprehensiveAdherenceDetails(patientId, 30);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#D4F0F8] flex items-center justify-center">
        <LoadingState message="Loading adherence details..." />
      </div>
    );
  }

  if (error || !comprehensiveData) {
    return (
      <div className="min-h-screen bg-[#D4F0F8] flex items-center justify-center p-4">
        <AlertCard
          variant="error"
          title="Error Loading Adherence Details"
          message="Unable to load patient adherence data. Please try again."
        />
      </div>
    );
  }

  // Transform backend data to component format
  const patientData = {
    // ✅ FIX: Add null check for patient_id to prevent crash
    patientId: comprehensiveData.patient_id?.toString() || '0',
    patientName: comprehensiveData.patient_name,
    age: comprehensiveData.age,
    cohort: comprehensiveData.cohort,
    overallAdherence: comprehensiveData.overall_adherence,
    medication: {
      overall: comprehensiveData.medication.overall,
      scheduledDoses: comprehensiveData.medication.scheduled_doses,
      takenDoses: comprehensiveData.medication.taken_doses,
      missedDoses: comprehensiveData.medication.missed_doses,
      // ✅ FIX: Add null check for medications array to prevent crash
      medications: (comprehensiveData.medication.medications || []).map(med => ({
        name: med.name,
        dosage: med.dosage,
        frequency: med.frequency,
        scheduled: med.scheduled,
        taken: med.taken,
        missedDates: med.missed_dates
      }))
    },
    exercise: {
      overall: comprehensiveData.exercise.overall,
      assignedExercises: comprehensiveData.exercise.assigned_exercises,
      completedExercises: comprehensiveData.exercise.completed_exercises,
      // ✅ FIX: Add null check for exercises array to prevent crash
      exercises: (comprehensiveData.exercise.exercises || []).map(ex => ({
        name: ex.name,
        type: ex.type,
        scheduledPerWeek: ex.scheduled_per_week,
        completedThisWeek: ex.completed_this_week,
        lastCompleted: ex.last_completed,
        missedDates: ex.missed_dates
      }))
    },
    homeTest: {
      overall: comprehensiveData.home_test.overall,
      scheduledTests: comprehensiveData.home_test.scheduled_tests,
      completedTests: comprehensiveData.home_test.completed_tests,
      // ✅ FIX: Add null check for tests array to prevent crash
      tests: (comprehensiveData.home_test.tests || []).map(test => ({
        testName: test.test_name,
        frequency: test.frequency,
        lastCompleted: test.last_completed,
        nextScheduled: test.next_scheduled,
        missedCount: test.missed_count,
        missedDates: test.missed_dates
      }))
    }
  };

  return (
    <DoctorLowAdherenceDetail
      patient={patientData}
      onBack={onBack}
      onMarkReviewed={onMarkReviewed}
    />
  );
}

export function DoctorDashboard({ onNavigate }: DoctorDashboardProps) {
  // Get authenticated doctor
  const { user } = useAuth();
  
  // Fetch dashboard data from API
  const { data: dashboardData, isLoading, error, refetch } = useDoctorDashboard(true);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [adherenceFilter, setAdherenceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [activeView, setActiveView] = useState<'list' | 'details' | 'adherence-detail' | 'nurse-tasks'>('list');
  const [activeSection, setActiveSection] = useState<'overview' | 'patients' | 'alerts' | 'symptoms' | 'low-adherence' | 'onboarding'>('overview');
  const [reviewedPatients, setReviewedPatients] = useState<Set<string>>(new Set());
  const [reviewedSymptomPatients, setReviewedSymptomPatients] = useState<Set<string>>(new Set());
  const [adherenceDetailPatient, setAdherenceDetailPatient] = useState<any>(null);
  const [selectedOnboardingPatient, setSelectedOnboardingPatient] = useState<any | null>(null);
  const [completedOnboarding, setCompletedOnboarding] = useState<string[]>([]);
  const [activeVideoCall, setActiveVideoCall] = useState<any>(null);
  
  // Medication management state - MUST be declared before any early returns
  const [tacrolimusDose, setTacrolimusDose] = useState('2.5');
  const [everolimusDose, setEverolimusDose] = useState('1.0');
  const [clinicalNoteText, setClinicalNoteText] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  
  // ✅ NEW: State for Airway Complications and Rejection Events
  const [airwayComplications, setAirwayComplications] = useState('');
  const [rejectionEvents, setRejectionEvents] = useState('');
  const [editingAirwayComplications, setEditingAirwayComplications] = useState('');
  const [editingRejectionEvents, setEditingRejectionEvents] = useState('');

  const [medications, setMedications] = useState<Medication[]>([]);
  
  // Fetch trend data from API for selected patient - MUST be declared before any early returns
  const patientId = selectedPatient?.patient_id || selectedPatient?.id;
  
  const { data: fev1TrendData } = useFEV1Trends(patientId || 0, 6, { enabled: !!patientId });
  const { data: sixMWTTrendData } = useSixMWTTrends(patientId || 0, 6, { enabled: !!patientId });
  const { data: vitalsTrendData } = useVitalsTrends(patientId || 0, 30, 'all', { enabled: !!patientId });
  const { data: sixMWTHistory } = useSixMWTHistory(patientId || 0, { limit: 10 });
  
  // ✅ FIX: Fetch discharge summaries for selected patient
  const { data: dischargeSummariesData } = useDischargeSummaries(patientId);
  const dischargeSummary = dischargeSummariesData?.[0]; // Get first discharge 
  
  console.log('📝 Discharge Summary for Patient ID', patientId, ':', dischargeSummary);
  
  // ✅ FIX: Fetch lab results for selected patient to get drug levels
  const { data: labResultsData } = useLabResults(patientId);
  
  // Extract tacrolimus and everolimus levels from lab results (API returns array directly)
  const tacrolimusLevel = labResultsData?.find(
    (result: any) => result.test_type === 'tacrolimus' || result.test_name?.toLowerCase().includes('tacrolimus')
  )?.result_value || '0.0';
  
  const everolimusLevel = labResultsData?.find(
    (result: any) => result.test_type === 'everolimus' || result.test_name?.toLowerCase().includes('everolimus')
  )?.result_value || '0.0';
  
  // ✅ FIX: Fetch symptom history for selected patient
  const { data: symptomHistoryData } = useSymptomHistory(patientId || 0, 30);
  
  // ✅ FIX: Fetch medications for selected patient to get current doses
  const { data: medicationsData } = useMedications(patientId || 0, { enabled: !!patientId });
  
  // ✅ NEW: Fetch clinical notes for selected patient
  const { data: clinicalNotesData, isLoading: notesLoading, error: notesError } = useClinicalNotes(patientId || 0, !!patientId);
  const createNoteMutation = useCreateClinicalNote();
  const updateNoteMutation = useUpdateClinicalNote();
  const deleteNoteMutation = useDeleteClinicalNote();

  const [showAddMedicationForm, setShowAddMedicationForm] = useState(false);
  const [editingMedication, setEditingMedication] = useState<Medication | null>(null);
  const [medicationForm, setMedicationForm] = useState({
    id: '',
    name: '',
    dosage: '',
    frequency: '',
    scheduled_times: '',
    notes: '',
  });
  
  // ✅ DEBUG: Log clinical notes data
  useEffect(() => {
    if (patientId) {
      console.log('📝 Clinical Notes Debug:', {
        patientId,
        clinicalNotesData,
        notesLoading,
        notesError,
        notesCount: clinicalNotesData?.notes?.length || 0
      });
    }
  }, [patientId, clinicalNotesData, notesLoading, notesError]);
  
  // ✅ FIX: Update dose states when medications OR lab results are loaded
  useEffect(() => {
    if (dischargeSummary?.medications) {
      const medications_ = dischargeSummary.medications.map(m => ({
        id : m.id,
        name: m.medication_name,
        dosage: m.dosage,
        frequency: m.frequency,
        notes: m.instructions,
        scheduled_times: m.scheduled_times
      }));

      setMedications(medications_);    
    }
    
    // Update from medication data
    if (dischargeSummary?.medications) {
      const tacrolimusMed = dischargeSummary?.medications.find(
        (med: any) => med.medication_name?.toLowerCase().includes('tacrolimus')
      );
      const everolimusMed = dischargeSummary?.medications.find(
        (med: any) => med.medication_name?.toLowerCase().includes('everolimus')
      );
      
      if (tacrolimusMed?.dosage) {
        // Extract numeric value from dosage string (e.g., "2.5mg" -> "2.5")
        const doseValue = tacrolimusMed.dosage.match(/[\d.]+/)?.[0];
        if (doseValue) setTacrolimusDose(doseValue);
      }
      
      if (everolimusMed?.dosage) {
        // Extract numeric value from dosage string (e.g., "1.0mg" -> "1.0")
        const doseValue = everolimusMed.dosage.match(/[\d.]+/)?.[0];
        if (doseValue) setEverolimusDose(doseValue);
      }
    }
  }, [dischargeSummary]);
  
  // Show loading state
  if (isLoading) {
    return <LoadingState message="Loading doctor dashboard..." />;
  }
  
  // Show error state
  if (error) {
    return (
      <div className="container mx-auto p-6">
        <AlertCard
          type="error"
          title="Failed to load dashboard"
          description={error?.message || "An error occurred while loading the dashboard"}
          action={
            <Button onClick={() => refetch()} variant="outline">
              Try Again
            </Button>
          }
        />
      </div>
    );
  }
  
  // Show empty state if no data
  if (!dashboardData) {
    return (
      <div className="container mx-auto p-6">
        <EmptyState 
          title="No dashboard data available" 
          description="Please contact support if this issue persists"
        />
      </div>
    );
  }

  // Doctor info from auth or fallback
  const doctor = {
    name: user?.full_name || 'Doctor',
    specialty: user?.doctor?.specialization || 'Pulmonologist',
  };

  // Use API data only
  const stats = dashboardData?.stats || {
    total_patients: 0,
    active_patients: 0,
    today_appointments: 0,
    pending_appointments: 0,
    completed_this_week: 0,
    low_adherence_count: 0,
    flagged_vitals_count: 0,
    pending_reviews_count: 0,
  };

  // Get all patients from API - backend returns 'assigned_patients'
  const allPatients = dashboardData?.all_patients || dashboardData?.assigned_patients || [];
  
  // 🔍 DEBUG: Log to see what adherence field the backend is returning (only if patients exist)
  if (allPatients.length > 0) {
    console.log('🔍 Doctor Dashboard - Sample Patient Data:', allPatients[0]);
  }
  
  // Map API patients to enhanced patient format
  const enhancedPatients = allPatients.map(patient => ({
    ...patient,
    // ✅ FIX: Map adherence from various possible backend field names
    adherence: patient.adherence || patient.adherence_percentage || patient.overall_adherence || 0,
    lastFEV1: patient.last_fev1 || patient.lastFEV1 || 0,
    lastFEV1Date: patient.last_fev1_date || patient.lastFEV1Date || null,
    fev1Change: patient.fev1_change || patient.fev1Change || 0,
    last6MWD: patient.last_6mwd || patient.last6MWD || 0,
    last6MWDDate: patient.last_6mwd_date || patient.last6MWDDate || null,
    sixMWDChange: patient.six_mwd_change || patient.sixMWDChange || 0,
    lastSpO2: patient.last_spo2 || patient.lastSpO2 || 0,
    hasSymptoms: patient.has_symptoms || patient.hasSymptoms || false,
    symptomsSeverity: patient.symptoms_severity || patient.symptomsSeverity || 'mild',
    alertType: patient.alert_type || patient.alertType || null,
  }));
  
  // 🔍 DEBUG: Log enhanced patient with adherence (only if patients exist)
  if (enhancedPatients.length > 0) {
    console.log('🔍 Doctor Dashboard - Enhanced Patient with Adherence:', {
      patientName: enhancedPatients[0]?.name,
      adherence: enhancedPatients[0]?.adherence,
      rawAdherence: allPatients[0]?.adherence,
      rawAdherencePercentage: allPatients[0]?.adherence_percentage,
      rawOverallAdherence: allPatients[0]?.overall_adherence
    });
  }

  // Filter patients
  const filteredPatients = enhancedPatients.filter(patient => {
    // ✅ FIX: Add null/undefined check for patient.name to prevent crashes
    const matchesSearch = patient.name?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false;
    const matchesAdherence = adherenceFilter === 'all' || 
      (adherenceFilter === 'high' && patient.adherence >= 85) ||
      (adherenceFilter === 'medium' && patient.adherence >= 70 && patient.adherence < 85) ||
      (adherenceFilter === 'low' && patient.adherence < 70);
    const matchesStatus = statusFilter === 'all' || patient.status === statusFilter;
    return matchesSearch && matchesAdherence && matchesStatus;
  });

  // Alert patients - use API data only, and ensure adherence field is mapped correctly
  const alertPatients = (dashboardData?.flagged_vitals || []).map(patient => ({
    ...patient,
    // ✅ FIX: Ensure adherence is mapped from backend field names
    adherence: patient.adherence || patient.adherence_percentage || patient.overall_adherence || 0,
  }));

  // Symptom patients (excluding reviewed ones) - use API data only
  const symptomPatients = (dashboardData?.pending_reviews || [])
    .map(patient => ({
      ...patient,
      // ✅ FIX: Ensure adherence is mapped from backend field names
      adherence: patient.adherence || patient.adherence_percentage || patient.overall_adherence || 0,
    }))
    .filter(p => !reviewedSymptomPatients.has(p.patient_id || p.id));

  // Low adherence patients - use API data only, and ensure adherence field is mapped correctly
  const lowAdherencePatients = (dashboardData?.low_adherence_patients || [])
    .map(patient => ({
      ...patient,
      // ✅ FIX: Ensure adherence is mapped from backend field names
      adherence: patient.adherence || patient.adherence_percentage || patient.overall_adherence || 0,
    }))
    .filter(p => !reviewedPatients.has(p.patient_id || p.id));

  // Calculate unread messages from API
  const unreadMessagesCount = dashboardData?.unread_messages_count || 0;

  // FEV1 trend data from API
  const getFEV1TrendData = () => fev1TrendData?.data || [];

  // 6MWD trend data from API
  const get6MWDTrendData = () => sixMWTTrendData?.data || [];

  // Map 6MWT history from API to report format expected by component
  const getSixMWTReports = () => {
    if (!sixMWTHistory?.results) return [];
    
    return sixMWTHistory.results.map((test, index) => ({
      id: `6mwt-${test.id}`,
      testDate: test.test_date,
      week: index + 1,
      patientInfo: {
        name: selectedPatient?.name || 'Unknown Patient',
        umrNo: selectedPatient?.umr_no || selectedPatient?.umrNo || 'N/A',
        age: selectedPatient?.age || 0,
        gender: (selectedPatient?.gender || 'Male') as 'Male' | 'Female',
        height: selectedPatient?.height || 0,
        weight: selectedPatient?.weight || 0,
        bmi: selectedPatient?.bmi || 0,
      },
      vitals: {
        heartRate: {
          baseline: test.pre_test_hr || 0,
          duringTest: test.post_test_hr || 0,
          postTest: test.post_test_hr || 0,
        },
        bloodPressure: {
          baseline: { systolic: 120, diastolic: 80 },
          postTest: { systolic: 130, diastolic: 85 },
        },
        saturation: {
          baseline: test.pre_test_spo2 || 0,
          duringTest: test.lowest_spo2 || test.pre_test_spo2 || 0,
          postTest: test.post_test_spo2 || 0,
        },
        borgScale: {
          baseline: `${test.borg_score || 0}/10`,
          duringTest: `${test.borg_score || 0}/10`,
          postTest: `${test.borg_score || 0}/10`,
        },
      },
      results: {
        predictedDistance: 661,
        lowerLimitNormal: 508,
        actualDistance: test.distance_meters || 0,
        predictedPercentage: ((test.distance_meters || 0) / 661) * 100,
      },
      sppbScore: '12 MINIMAL LIMITATIONS',
      gripStrength: {
        right: 0,
        left: 0,
      },
      physiotherapist: {
        name: 'Physiotherapist',
        credentials: 'Certified Physiotherapist',
      },
      status: 'approved' as const,
      notes: test.notes || 'No notes available',
    }));
  };

  // Transform vitals data from API
  const getVitalsData = () => {
    if (!vitalsTrendData?.data || !Array.isArray(vitalsTrendData.data)) return [];
    
    // Map and sort by date
    const mappedData = vitalsTrendData.data.map(vital => ({
      date: new Date(vital.date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' }),
      fullDate: vital.date,
      heartRate: vital.heart_rate || null,
      spO2: vital.spo2 || null,
      systolic: vital.systolic || null,
      diastolic: vital.diastolic || null,
      temp: vital.temperature || null,
    })).sort((a, b) => new Date(a.fullDate).getTime() - new Date(b.fullDate).getTime());
    
    return mappedData;
  };

  // Get adherence detail data from API via patient's adherence_breakdown
  const getPatientAdherenceDetail = (patient: any) => {
    // Get cohort name from patient data (already included in API response)
    const cohortName = patient.cohort_name || 'Unknown Cohort';
    
    // Use adherence_breakdown from API if available
    const breakdown = patient.adherence_breakdown || {};
    
    // 🔍 DEBUG: Log adherence breakdown for this patient
    console.log('🔍 Adherence Breakdown for patient:', patient.name || 'Unknown', {
      rawBreakdown: patient.adherence_breakdown,
      patientAdherence: patient.adherence,
      medication: breakdown.medication,
      exercise: breakdown.exercise,
      home_tests: breakdown.home_tests
    });
    
    // Use medication adherence data from API
    const medScheduled = breakdown.medication?.doses_scheduled || breakdown.medication?.scheduled || 0;
    const medTaken = breakdown.medication?.doses_taken || breakdown.medication?.taken || breakdown.medication?.completed || 0;
    const medicationAdherence = {
      overall: breakdown.medication?.adherence_percentage || breakdown.medication?.percentage || patient.adherence || 0,
      scheduledDoses: medScheduled,
      takenDoses: medTaken,
      missedDoses: medScheduled - medTaken,
      medications: [] // This would come from a separate medications API endpoint in future
    };

    // Use exercise adherence data from API
    const exerciseAdherence = {
      overall: breakdown.exercise?.adherence_percentage || breakdown.exercise?.percentage || patient.adherence || 0,
      assignedExercises: breakdown.exercise?.sessions_scheduled || breakdown.exercise?.scheduled || 21, // Default to 21 if not in API
      completedExercises: breakdown.exercise?.sessions_completed || breakdown.exercise?.completed || 0,
      exercises: [] // This would come from a separate exercises API endpoint in future
    };

    // Use home test adherence data from API
    const homeTestAdherence = {
      overall: breakdown.home_tests?.adherence_percentage || breakdown.home_tests?.percentage || patient.adherence || 0,
      scheduledTests: breakdown.home_tests?.tests_scheduled || breakdown.home_tests?.scheduled || 6, // Default to 6 if not in API
      completedTests: breakdown.home_tests?.tests_completed || breakdown.home_tests?.completed || 0,
      tests: [] // This would come from a separate home tests API endpoint in future
    };

    const result = {
      patientId: patient.id,
      patientName: patient.name,
      age: patient.age,
      cohort: cohortName,
      // ✅ FIX: Ensure we use mapped adherence value
      overallAdherence: patient.adherence || patient.adherence_percentage || patient.overall_adherence || 0,
      medication: medicationAdherence,
      exercise: exerciseAdherence,
      homeTest: homeTestAdherence
    };
    
    // 🔍 DEBUG: Log calculated adherence detail
    console.log('🔍 Calculated Adherence Detail:', result);
    
    return result;
  };

  const totalPatients = enhancedPatients.length;
  const activeAlerts = alertPatients.length;
  // ✅ FIX: Prevent division by zero - return 0 if no patients
  const avgAdherence = totalPatients > 0 
    ? Math.round(enhancedPatients.reduce((sum, p) => sum + p.adherence, 0) / totalPatients)
    : 0;
  
  // Calculate appointments from API data
  const todaysAppointmentsCount = dashboardData?.today_appointments?.length || 0;
  // ✅ Use total_scheduled_appointments from backend stats (today + future scheduled/confirmed)
  const totalAppointmentsCount = stats.total_scheduled_appointments || todaysAppointmentsCount;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'On Track':
        return 'bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]';
      case 'Missed':
        return 'bg-[#F07D13]/10 text-[#F07D13] border-[#F07D13]';
      case 'Escalated':
        return 'bg-[#F07D13]/10 text-[#F07D13] border-[#F07D13]';
      default:
        return '';
    }
  };

  const getAdherenceColor = (adherence: number) => {
    if (adherence >= 85) return 'text-[#14CAF4]';
    if (adherence >= 70) return 'text-[#F07D13]';
    return 'text-[#F07D13]';
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'severe':
        return 'bg-[#F07D13]/10 text-[#F07D13] border-[#F07D13]';
      case 'moderate':
        return 'bg-[#F07D13]/10 text-[#F07D13] border-[#F07D13]';
      case 'mild':
        return 'bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]';
      default:
        return '';
    }
  };

  // Helper function to calculate Post Tx Duration
  const calculatePostTxDuration = (transplantDate: string | null): string => {
    if (!transplantDate) return '—';
    
    try {
      const txDate = new Date(transplantDate);
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - txDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      const years = Math.floor(diffDays / 365);
      const months = Math.floor((diffDays % 365) / 30);
      const days = diffDays % 30;
      
      if (years > 0) {
        return months > 0 ? `${years}y ${months}m` : `${years}y`;
      } else if (months > 0) {
        return `${months}m`;
      } else {
        return `${days}d`;
      }
    } catch (error) {
      return '—';
    }
  };

  const handleViewPatient = (patient: any) => {
    setSelectedPatient(patient);
    setActiveView('details');
    toast.info(`Loading ${patient.name || 'patient'}'s profile...`);
  };

  const handleViewHomeNurseTasks = (patient: any) => {
    if (!patient?.id) {
      toast.error('Unable to load home nurse tasks. Patient ID is missing.');
      return;
    }
    setSelectedPatient(patient);
    setActiveView('nurse-tasks');
    toast.info(`Loading ${patient.name || 'patient'}'s home nurse tasks...`);
  };

  const handleBackToList = () => {
    setActiveView('list');
    setSelectedPatient(null);
    setAdherenceDetailPatient(null);
  };

  const handleViewAdherenceDetail = (patient: any) => {
    // Store patient ID to trigger data fetch
    setAdherenceDetailPatient(patient);
    setActiveView('adherence-detail');
  };

  const handleMarkAsReviewed = (patientId: string, notes: string) => {
    setReviewedPatients(prev => new Set(prev).add(patientId));
    // In a real app, this would save to backend
    console.log(`Patient ${patientId} marked as reviewed with notes:`, notes);
  };

  // Helper function to check if appointment can be joined
  const canJoinAppointment = (datetime: string) => {
    const now = new Date();
    const [date, time] = datetime.split("T");
    // Parse the appointment date and time
    const [year, month, day] = date.split('-').map(Number);
    const timeStr = time.toLowerCase();
    const [timeOnly, period] = timeStr.split(' ');
    const [hours, minutes] = timeOnly.split(':').map(Number);
    
    // Convert to 24-hour format
    let appointmentHours = hours;
    if (period === 'pm' && hours !== 12) {
      appointmentHours = hours + 12;
    } else if (period === 'am' && hours === 12) {
      appointmentHours = 0;
    }
    
    const appointmentDate = new Date(year, month - 1, day, appointmentHours, minutes);
    
    // Allow joining if appointment time is now or in the past
    return appointmentDate >= now;
  };

  // Handle joining video call
  const handleJoinCall = (appointment: any) => {
    setActiveVideoCall(appointment);
  };

  // Handle ending video call
  const handleEndCall = () => {
    setActiveVideoCall(null);
    toast.success('Call ended successfully');
  };
  
  const resetMedicationForm = () => {
    setMedicationForm({
      id : '',
      name: '',
      dosage: '',
      frequency: '',
      scheduled_times: '',
      notes: ''      
    });
    setEditingMedication(null);
    setShowAddMedicationForm(false);
  };

  const handleAddMedication = async() => {
    if (!medicationForm.name || !medicationForm.dosage || !medicationForm.frequency || !medicationForm.scheduled_times) {
      toast.error('Please fill in all required fields');
      return;
    }

    const newMedication: Medication = {
      id: '0',
      name: medicationForm.name,
      dosage: medicationForm.dosage,
      frequency: medicationForm.frequency,
      scheduled_times: medicationForm.scheduled_times,
      notes: medicationForm.notes || undefined,
    };

    try {      
      await medicationsApi.createMedicationSchedule({
        patient_id: parseInt(patientId),
        medication_name: medicationForm.name,
        dosage: medicationForm.dosage,
        frequency: medicationForm.frequency,
        scheduled_times: medicationForm.scheduled_times.split(","),
        instructions: medicationForm.notes,
      });
    } catch (error: any) {
      console.error('Error adding medication:', error);
      toast.error('Failed to add medication', {
        description: error?.response?.data?.message || error.message || 'Please try again',
      });
      return;
    }

    setMedications([...medications, newMedication]);
    toast.success('Medication added successfully!', {
      description: `${newMedication.name} has been added to the medication list.`,
    });
    resetMedicationForm();
  };

  const handleUpdateMedication = async() => {
    if (!editingMedication) return;

    if (!medicationForm.name || !medicationForm.dosage || !medicationForm.frequency || !medicationForm.scheduled_times) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {      
      await medicationsApi.updateMedicationSchedule(parseInt(medicationForm.id), {
        medication_name: medicationForm.name,
        dosage: medicationForm.dosage,
        frequency: medicationForm.frequency,
        scheduled_times: medicationForm.scheduled_times.split(","),
        instructions: medicationForm.notes,
      });
    } catch (error: any) {
      console.error('Error updating medication:', error);
      toast.error('Failed to update medication', {
        description: error?.response?.data?.message || error.message || 'Please try again',
      });
      return;
    }

    const updatedMedications = medications.map(med => 
      med.id === editingMedication.id 
        ? {
            ...med,
            id : medicationForm.id,
            name: medicationForm.name,
            dosage: medicationForm.dosage,
            frequency: medicationForm.frequency,
            scheduled_times: medicationForm.scheduled_times,
            notes: medicationForm.notes || undefined,
          }
        : med
    );

    setMedications(updatedMedications);
    toast.success('Medication updated successfully!', {
      description: `${medicationForm.name} has been updated.`,
    });
    resetMedicationForm();
  };

  const handleEditMedication = (medication: Medication) => {
    setEditingMedication(medication);
    setMedicationForm({
      id : medication.id,
      name: medication.name,
      dosage: medication.dosage,
      frequency: medication.frequency,
      scheduled_times: medication.scheduled_times,
      notes: medication.notes || '',
    });
    setShowAddMedicationForm(true);
  };

  const handleDeleteMedication = async(medicationId: string) => {
    const medication = medications.find(med => med.id === medicationId);
    if (!medication) return;

    try {      
      await medicationsApi.deleteMedicationSchedule(parseInt(medicationId));
    } catch (error: any) {
      console.error('Error deleting medication:', error);
      toast.error('Failed to delete medication', {
        description: error?.response?.data?.message || error.message || 'Please try again',
      });
      return;
    }

    setMedications(medications.filter(med => med.id !== medicationId));
    toast.success('Medication deleted successfully!', {
      description: `${medication.name} has been removed from the medication list.`,
    });
  };
    
  // Show video call screen if active
  if (activeVideoCall) {    
    return (
      <VideoCallScreen
        sessionId='TestSession#1'
        patientName={activeVideoCall.patient_name}
        doctorName={activeVideoCall.doctor_name}
        onEndCall={handleEndCall}
      />
    );
  }

  // Adherence Detail View
  if (activeView === 'adherence-detail' && adherenceDetailPatient) {
    return (
      <AdherenceDetailWrapper
        patientId={adherenceDetailPatient.patient_id}
        onBack={handleBackToList}
        onMarkReviewed={handleMarkAsReviewed}
      />
    );
  }

  // Discharge Summary Onboarding View
  if (selectedOnboardingPatient) {
    return (
      <DischargeSummaryOnboarding
        patient={selectedOnboardingPatient}
        onBack={() => setSelectedOnboardingPatient(null)}
        onComplete={(patientId) => {
          setCompletedOnboarding([...completedOnboarding, patientId]);
          setSelectedOnboardingPatient(null);
          toast.success('Patient onboarding completed successfully!');
        }}
      />
    );
  }

  // Home Nurse Tasks View
  if (activeView === 'nurse-tasks' && selectedPatient) {
    return (
      <div className="min-h-screen bg-[#D4F0F8]">
        <div className="bg-white border-b border-border sticky top-0 z-40 shadow-sm">
          <div className="px-4 md:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={handleBackToList} size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Back to Patient List</span>
                <span className="sm:hidden">Back</span>
              </Button>
              <div>
                <h1 className="text-xl font-semibold text-primary">{selectedPatient.name || 'Patient'}</h1>
                <p className="text-sm text-muted-foreground">Home Nurse Tasks</p>
              </div>
            </div>
          </div>
        </div>
        <div className="px-4 md:px-6 lg:px-8 py-6">
          <HomeNurseTasksViewCompact
            patientId={selectedPatient?.id?.toString() || '0'}
            patientName={selectedPatient?.name || 'Patient'}
            patientAge={selectedPatient?.age}
            patientGender={selectedPatient?.gender}
          />
        </div>
      </div>
    );
  }

  // Patient Details View
  if (activeView === 'details' && selectedPatient) {
    return (
      <div className="min-h-screen bg-[#D4F0F8]">
        {/* Top Navigation for Patient Details */}
        <div className="bg-white border-b border-border sticky top-0 z-40 shadow-sm">
          <div className="px-4 md:px-6 lg:px-8 py-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <Button variant="outline" onClick={handleBackToList} size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">Back to Patient List</span>
                  <span className="sm:hidden">Back</span>
                </Button>
                <div>
                  <h1 className="text-primary">{selectedPatient.name}</h1>
                  <p className="text-muted-foreground">
                    {selectedPatient.age} years • {selectedPatient.cohort_name || 'No Cohort'}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Badge variant="outline" className={getStatusColor(selectedPatient.status)}>
                  {selectedPatient.status}
                </Badge>
                <Badge variant="outline" className="bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]">
                  {selectedPatient.adherence}% Adherence
                </Badge>
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 md:px-6 lg:px-8 py-6 space-y-6">
          {/* Patient Demographics Card */}
          <Card className="shadow-lg">
            <CardHeader className="bg-gradient-to-r from-primary to-primary/80 text-white">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <Avatar className="w-16 h-16 border-2 border-white">
                    <AvatarImage src={selectedPatient.avatar} />
                    <AvatarFallback className="bg-white text-primary text-lg">
                      {selectedPatient.name?.split(' ').map((n: string) => n[0]).join('') || 'P'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h2 className="text-xl text-white mb-1">{selectedPatient.name}</h2>
                    <div className="flex flex-wrap gap-3 text-white/90 text-sm">
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {selectedPatient.age || 'N/A'} years • {selectedPatient.gender || 'Not specified'}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        UMR: {selectedPatient.umr_no || selectedPatient.umrNo || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Contact Information */}
                <div className="space-y-1">
                  <p className="text-muted-foreground text-xs">Contact Number</p>
                  <p className="text-foreground flex items-center gap-2">
                    <Phone className="w-4 h-4 text-primary" />
                    {selectedPatient.phone || selectedPatient.contactNumber || 'Not provided'}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-muted-foreground text-xs">Email</p>
                  <p className="text-foreground flex items-center gap-2">
                    <Mail className="w-4 h-4 text-primary" />
                    {selectedPatient.email || 'Not provided'}
                  </p>
                </div>
                {/* Clinical Metrics */}
                <div className="space-y-1">
                  <p className="text-muted-foreground text-xs">Latest FEV1</p>
                  <div className="flex items-center gap-2">
                    <Activity className="w-4 h-4 text-primary" />
                    <span className="text-foreground">{selectedPatient.lastFEV1 || 0}%</span>
                    {selectedPatient.fev1Change !== undefined && selectedPatient.fev1Change !== 0 && (
                      <Badge variant="outline" className={selectedPatient.fev1Change > 0 ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}>
                        {selectedPatient.fev1Change > 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                        {Math.abs(selectedPatient.fev1Change)}%
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-muted-foreground text-xs">Latest 6MWD</p>
                  <div className="flex items-center gap-2">
                    <Footprints className="w-4 h-4 text-primary" />
                    <span className="text-foreground">{selectedPatient.last6MWD || 0}m</span>
                    {selectedPatient.sixMWDChange !== undefined && selectedPatient.sixMWDChange !== 0 && (
                      <Badge variant="outline" className={selectedPatient.sixMWDChange > 0 ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}>
                        {selectedPatient.sixMWDChange > 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                        {Math.abs(selectedPatient.sixMWDChange)}m
                      </Badge>
                    )}
                  </div>
                </div>
                {/* Transplant Info */}
                {dischargeSummary?.transplantDate && (
                  <div className="space-y-1">
                    <p className="text-muted-foreground text-xs">Transplant Date</p>
                    <p className="text-foreground">
                      {new Date(dischargeSummary.transplantDate).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                )}
                {dischargeSummary?.transplantDate && (
                  <div className="space-y-1">
                    <p className="text-muted-foreground text-xs">Post Tx Duration</p>
                    <p className="text-foreground">{calculatePostTxDuration(dischargeSummary.transplantDate)}</p>
                  </div>
                )}
                {/* Vitals */}
                <div className="space-y-1">
                  <p className="text-muted-foreground text-xs">Latest SpO2</p>
                  <div className="flex items-center gap-2">
                    <Droplet className="w-4 h-4 text-primary" />
                    <span className="text-foreground">{selectedPatient.lastSpO2 || 'N/A'}%</span>
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-muted-foreground text-xs">Blood Group</p>
                  <p className="text-foreground">{selectedPatient.bloodGroup || 'Not specified'}</p>
                </div>
              </div>
              {/* Address */}
              {selectedPatient.address && (
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-muted-foreground text-xs mb-1">Address</p>
                  <p className="text-foreground flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-primary mt-0.5" />
                    {selectedPatient.address}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <Tabs defaultValue="summary" className="space-y-6">
            <TabsList className="w-full flex flex-wrap md:grid md:grid-cols-6 gap-1">
              <TabsTrigger value="summary" className="flex-1 md:flex-none whitespace-nowrap">
                <FileText className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Clinical Summary</span>
                <span className="sm:hidden">Summary</span>
              </TabsTrigger>
              <TabsTrigger value="symptoms" className="flex-1 md:flex-none whitespace-nowrap">
                <AlertCircle className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Symptoms</span>
                <span className="sm:hidden">Symptoms</span>
              </TabsTrigger>
              <TabsTrigger value="6mwt" className="flex-1 md:flex-none whitespace-nowrap">
                <Footprints className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">6MWT Reports</span>
                <span className="sm:hidden">6MWT</span>
              </TabsTrigger>
              <TabsTrigger value="vitals" className="flex-1 md:flex-none whitespace-nowrap">
                <span className="hidden sm:inline">Trends</span>
                <span className="sm:hidden">Trends</span>
              </TabsTrigger>
              <TabsTrigger value="notes" className="flex-1 md:flex-none whitespace-nowrap">
                <span className="hidden sm:inline">Clinical Notes</span>
                <span className="sm:hidden">Notes</span>
              </TabsTrigger>
              <TabsTrigger value="medications" className="flex-1 md:flex-none whitespace-nowrap">
                <Pill className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Medications</span>
                <span className="sm:hidden">Meds</span>
              </TabsTrigger>
            </TabsList>

            {/* Clinical Summary Tab */}
            <TabsContent value="summary" className="space-y-6">
                {dischargeSummary && (
                <ClinicalSummaryTab 
                  clinicalNotesData = {clinicalNotesData}
                  patient={selectedPatient}
                  dischargeSummary={dischargeSummary}
                />
              )}
            </TabsContent>

            {/* Symptoms Tab */}
            <TabsContent value="symptoms" className="space-y-6">
              {symptomHistoryData && symptomHistoryData.symptoms && symptomHistoryData.symptoms.length > 0 ? (
                <div className="space-y-4">
                  {symptomHistoryData.symptoms.map((log: any) => {
                    const logDate = new Date(log.date || log.created_at || log.recorded_at);
                    const symptoms = log.symptoms || {};
                    
                    // Calculate average severity (with safe fallback for missing values)
                    const avgSeverity = Math.round(
                      ((symptoms.breathlessness || 0) + (symptoms.cough || 0) + (symptoms.chest_pain || 0) + 
                       (symptoms.fatigue || 0) + (10 - (symptoms.energy_level || 5)) + (10 - (symptoms.sleep_quality || 5))) / 6
                    );
                    
                    const getSeverityBadgeClass = (severity: number) => {
                      if (severity >= 7) return 'bg-red-50 border-red-200 text-red-700';
                      if (severity >= 4) return 'bg-orange-50 border-orange-200 text-orange-700';
                      return 'bg-green-50 border-green-200 text-green-700';
                    };
                    
                    const getSeverityLabel = (severity: number) => {
                      if (severity >= 7) return 'Severe';
                      if (severity >= 4) return 'Moderate';
                      return 'Mild';
                    };
                    
                    return (
                      <Card key={log.id} className="border-l-4 border-l-[#14CAF4] shadow-lg">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <p className="text-foreground font-medium">
                                {format(logDate, 'PPPP')}
                              </p>
                              <p className="text-muted-foreground text-sm">
                                Logged at {format(logDate, 'p')}
                              </p>
                            </div>
                            <Badge variant="outline" className={getSeverityBadgeClass(avgSeverity)}>
                              {getSeverityLabel(avgSeverity)}
                            </Badge>
                          </div>
                          
                          {/* Symptom Metrics Grid */}
                          {Object.keys(symptoms).length > 0 && (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
                              {Object.entries(symptoms).map(([key, value]: [string, any]) => {
                                const numValue = typeof value === 'number' ? value : 0;
                                return (
                                  <div key={key} className="p-2 bg-secondary rounded-lg">
                                    <p className="text-xs text-muted-foreground mb-1 capitalize">
                                      {key.replace(/_/g, ' ')}
                                    </p>
                                    <div className="flex items-center gap-2">
                                      <div className="flex-1 bg-white rounded-full h-2">
                                        <div 
                                          className={`h-2 rounded-full transition-all ${
                                            key === 'energy_level' || key === 'sleep_quality' 
                                              ? 'bg-[#14CAF4]' 
                                              : 'bg-primary'
                                          }`}
                                          style={{ width: `${numValue * 10}%` }}
                                        />
                                      </div>
                                      <span className="text-sm text-foreground">{numValue}/10</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          
                          {/* Triggers */}
                          {log.triggers && log.triggers.length > 0 && (
                            <div className="mb-3">
                              <p className="text-xs text-muted-foreground mb-2">Triggers:</p>
                              <div className="flex flex-wrap gap-2">
                                {log.triggers.map((trigger: string, idx: number) => (
                                  <Badge 
                                    key={idx} 
                                    variant="outline"
                                    className="bg-amber-50 text-amber-700 border-amber-200"
                                  >
                                    {trigger}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Additional Notes */}
                          {log.additional_notes && (
                            <div className="p-3 bg-muted rounded-lg">
                              <p className="text-xs text-muted-foreground mb-1">Additional Notes:</p>
                              <p className="text-sm text-foreground">{log.additional_notes}</p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              ) : (
                <Card className="shadow-lg">
                  <CardContent className="p-6">
                    <EmptyState
                      icon={AlertCircle}
                      title="No Symptoms Logged"
                      description="This patient hasn't logged any symptoms in the last 30 days."
                    />
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* 6MWT Reports Tab */}
            <TabsContent value="6mwt" className="space-y-6">
              <SixMWTReportView
                patientId={selectedPatient.id}
                patientName={selectedPatient.name}
                reports={getSixMWTReports()}
                onApprove={(reportId) => {
                  toast.success('6MWT Report approved!', {
                    description: 'Report has been signed off successfully',
                  });
                }}
                onReject={(reportId) => {
                  toast.warning('Re-assessment requested', {
                    description: 'Patient will be notified to perform the test again',
                  });
                }}
              />
            </TabsContent>

            {/* Vitals Trends Tab */}
            <TabsContent value="vitals" className="space-y-6">
              {/* FEV1 Trends Card */}
              <FEV1TrendsCard 
                patientId={selectedPatient.id}
                patientName={selectedPatient.name}
              />

              {/* 6MWD Trends Card */}
              <SixMWDTrendsCard 
                patientId={selectedPatient.id}
                patientName={selectedPatient.name}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* SpO2 Trend */}
                <Card className="shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-primary flex items-center gap-2">
                      <Droplet className="w-5 h-5" />
                      SpO2 Trends
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={getVitalsData()}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" />
                          <XAxis dataKey="date" stroke="#494949" />
                          <YAxis domain={['dataMin - 5', 'dataMax + 5']} stroke="#494949" />
                          <Tooltip />
                          <ReferenceLine y={95} stroke="#E84C3D" strokeDasharray="5 5" label="Threshold" />
                          <Line
                            type="monotone"
                            dataKey="spO2"
                            stroke="#14CAF4"
                            strokeWidth={2}
                            dot={{ fill: '#14CAF4', r: 4 }}
                            connectNulls
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Heart Rate Trend */}
                <Card className="shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-primary flex items-center gap-2">
                      <Heart className="w-5 h-5" />
                      Heart Rate Trends
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={getVitalsData()}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" />
                          <XAxis dataKey="date" stroke="#494949" />
                          <YAxis domain={['dataMin - 10', 'dataMax + 10']} stroke="#494949" />
                          <Tooltip />
                          <Line
                            type="monotone"
                            dataKey="heartRate"
                            stroke="#F07D13"
                            strokeWidth={2}
                            dot={{ fill: '#F07D13', r: 4 }}
                            connectNulls
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Blood Pressure Trend */}
                <Card className="shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-primary flex items-center gap-2">
                      <Activity className="w-5 h-5" />
                      Blood Pressure Trends
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={getVitalsData()}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" />
                          <XAxis dataKey="date" stroke="#494949" />
                          <YAxis domain={['dataMin - 10', 'dataMax + 10']} stroke="#494949" />
                          <Tooltip />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="systolic"
                            stroke="#2762AC"
                            strokeWidth={2}
                            dot={{ fill: '#2762AC', r: 4 }}
                            name="Systolic"
                            connectNulls
                          />
                          <Line
                            type="monotone"
                            dataKey="diastolic"
                            stroke="#14CAF4"
                            strokeWidth={2}
                            dot={{ fill: '#14CAF4', r: 4 }}
                            name="Diastolic"
                            connectNulls
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Temperature Trend */}
                <Card className="shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-primary flex items-center gap-2">
                      <ThermometerSun className="w-5 h-5" />
                      Temperature Trends
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={getVitalsData()}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" />
                          <XAxis dataKey="date" stroke="#494949" />
                          <YAxis domain={['dataMin - 1', 'dataMax + 1']} stroke="#494949" />
                          <Tooltip />
                          <Line
                            type="monotone"
                            dataKey="temp"
                            stroke="#F07D13"
                            strokeWidth={2}
                            dot={{ fill: '#F07D13', r: 4 }}
                            connectNulls
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Lab Results Tab */}
            <TabsContent value="labs" className="space-y-6">
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="text-primary">
                    <span className="text-base sm:text-lg">Lab Test Results</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {labResultsData && labResultsData.length > 0 ? (
                    <div className="overflow-x-auto -mx-4 sm:mx-0">
                      <div className="inline-block min-w-full align-middle">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Test Name</TableHead>
                              <TableHead>Date</TableHead>
                              <TableHead>Result</TableHead>
                              <TableHead>Reference Range</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead>Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {labResultsData.map((result: any) => {
                              const isAbnormal = result.abnormal_flag;
                              const statusColor = isAbnormal 
                                ? 'bg-[#F07D13]/10 text-[#F07D13] border-[#F07D13]'
                                : 'bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]';
                              
                              return (
                                <TableRow key={result.id}>
                                  <TableCell className="text-foreground">{result.test_name}</TableCell>
                                  <TableCell className="text-muted-foreground">
                                    {new Date(result.test_date).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric'
                                    })}
                                  </TableCell>
                                  <TableCell className="text-foreground">
                                    {result.result_value} {result.result_unit || ''}
                                  </TableCell>
                                  <TableCell className="text-muted-foreground">
                                    {result.reference_range || '-'}
                                  </TableCell>
                                  <TableCell>
                                    <Badge variant="outline" className={statusColor}>
                                      {isAbnormal ? 'Abnormal' : result.status || 'Normal'}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>
                                    {result.file_url && (
                                      <Button 
                                        variant="ghost" 
                                        size="sm"
                                        onClick={() => window.open(result.file_url, '_blank')}
                                      >
                                        <Eye className="w-4 h-4" />
                                      </Button>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <FlaskConical className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                      <p className="text-muted-foreground">No lab results available</p>
                      <p className="text-muted-foreground text-sm mt-1">Lab test results will appear here once uploaded</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Lab Results Trends */}
              <div className="mt-6">
                <LabResultsTrends 
                  patientId={selectedPatient.id}
                  patientName={selectedPatient.name}
                />
              </div>
            </TabsContent>

            {/* Clinical Notes Tab */}
            <TabsContent value="notes" className="space-y-6">
              {/* Immunosuppressant Medication Management */}
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="text-primary">Immunosuppressant Management</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Tacrolimus Section */}
                  <div className="p-4 bg-primary/5 rounded-xl space-y-4">
                    <h3 className="text-primary">Tacrolimus</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="tacrolimus-level" className="text-muted-foreground">
                          Current Level
                        </Label>
                        <div className="flex items-center gap-2">
                          <Input
                            id="tacrolimus-level"
                            value={tacrolimusLevel}
                            disabled
                            className="bg-secondary"
                          />
                          <span className="text-muted-foreground whitespace-nowrap">ng/mL</span>
                        </div>
                        <p className="text-muted-foreground text-xs">
                          Target range: 5-15 ng/mL
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="tacrolimus-dose" className="text-muted-foreground">
                          Dose Adjustment
                        </Label>
                        <div className="flex items-center gap-2">
                          <Input
                            id="tacrolimus-dose"
                            type="number"
                            step="0.5"
                            value={tacrolimusDose}
                            onChange={(e) => setTacrolimusDose(e.target.value)}
                            className="border-primary/30 focus:border-primary"
                          />
                          <span className="text-muted-foreground whitespace-nowrap">mg</span>
                        </div>
                        <p className="text-muted-foreground text-xs">
                          Current dose: {tacrolimusDose} mg BID
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Everolimus Section */}
                  <div className="p-4 bg-[#14CAF4]/5 rounded-xl space-y-4">
                    <h3 className="text-primary">Everolimus</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="everolimus-level" className="text-muted-foreground">
                          Current Level
                        </Label>
                        <div className="flex items-center gap-2">
                          <Input
                            id="everolimus-level"
                            value={everolimusLevel}
                            disabled
                            className="bg-secondary"
                          />
                          <span className="text-muted-foreground whitespace-nowrap">ng/mL</span>
                        </div>
                        <p className="text-muted-foreground text-xs">
                          Target range: 3-8 ng/mL
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="everolimus-dose" className="text-muted-foreground">
                          Dose Adjustment
                        </Label>
                        <div className="flex items-center gap-2">
                          <Input
                            id="everolimus-dose"
                            type="number"
                            step="0.5"
                            value={everolimusDose}
                            onChange={(e) => setEverolimusDose(e.target.value)}
                            className="border-[#14CAF4]/30 focus:border-[#14CAF4]"
                          />
                          <span className="text-muted-foreground whitespace-nowrap">mg</span>
                        </div>
                        <p className="text-muted-foreground text-xs">
                          Current dose: {everolimusDose} mg OD
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end gap-2 pt-2">
                    <Button 
                      variant="outline"
                      onClick={() => {
                        // Reset to original values from medications data
                        if (medicationsData?.schedules) {
                          const tacrolimusMed = medicationsData.schedules.find(
                            (med: any) => med.medication_name?.toLowerCase().includes('tacrolimus')
                          );
                          const everolimusMed = medicationsData.schedules.find(
                            (med: any) => med.medication_name?.toLowerCase().includes('everolimus')
                          );
                          
                          if (tacrolimusMed?.dosage) {
                            setTacrolimusDose(tacrolimusMed.dosage.replace(/[^\d.]/g, ''));
                          }
                          if (everolimusMed?.dosage) {
                            setEverolimusDose(everolimusMed.dosage.replace(/[^\d.]/g, ''));
                          }
                          toast.info('Doses reset to current prescribed values');
                        } else {
                          toast.info('No medication data to reset to');
                        }
                      }}
                    >
                      Reset
                    </Button>
                    <Button 
                      className="bg-primary hover:bg-primary/90"
                      onClick={async () => {
                        if (!selectedPatient?.id) {
                          toast.error('No patient selected');
                          return;
                        }

                        try {
                          const { apiClient } = await import('../lib/api-client');
                          
                          // Find tacrolimus and everolimus medication IDs
                          const tacrolimusMed = medicationsData?.schedules?.find(
                            (med: any) => med.medication_name?.toLowerCase().includes('tacrolimus')
                          );
                          const everolimusMed = medicationsData?.schedules?.find(
                            (med: any) => med.medication_name?.toLowerCase().includes('everolimus')
                          );
                          
                          const updatePromises = [];
                          
                          // Update Tacrolimus if medication exists and dose changed
                          if (tacrolimusMed?.id) {
                            updatePromises.push(
                              apiClient.put(`/medications/${tacrolimusMed.id}/dose`, {
                                dosage: `${tacrolimusDose} mg BID`,
                                reason: 'Dose adjustment by doctor',
                                prescribed_by_id: user?.doctor_id
                              })
                            );
                          }
                          
                          // Update Everolimus if medication exists and dose changed
                          if (everolimusMed?.id) {
                            updatePromises.push(
                              apiClient.put(`/medications/${everolimusMed.id}/dose`, {
                                dosage: `${everolimusDose} mg OD`,
                                reason: 'Dose adjustment by doctor',
                                prescribed_by_id: user?.doctor_id
                              })
                            );
                          }
                          
                          if (updatePromises.length === 0) {
                            toast.error('No medications found to update', {
                              description: 'Tacrolimus or Everolimus not prescribed for this patient'
                            });
                            return;
                          }
                          
                          await Promise.all(updatePromises);
                          
                          toast.success('Medication doses updated successfully!', {
                            description: `Tacrolimus: ${tacrolimusDose}mg BID, Everolimus: ${everolimusDose}mg OD`,
                          });
                          
                          // Refresh medications data
                          window.location.reload();
                        } catch (error: any) {
                          console.error('Error updating medication doses:', error);
                          toast.error('Failed to update medication doses', {
                            description: error?.response?.data?.message || error.message || 'Please try again'
                          });
                        }
                      }}
                    >
                      Update Doses
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="text-primary">Add Clinical Note</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="clinical-note" className="text-muted-foreground">
                      Clinical Observations
                    </Label>
                    <textarea
                      id="clinical-note"
                      className="w-full p-4 border border-border rounded-xl min-h-32"
                      placeholder="Enter clinical observations, treatment adjustments, or recommendations..."
                      value={clinicalNoteText}
                      onChange={(e) => setClinicalNoteText(e.target.value)}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="airway-complications" className="text-muted-foreground">
                        Airway Complications
                      </Label>
                      <select
                        id="airway-complications"
                        className="w-full p-3 border border-border rounded-xl bg-background"
                        value={airwayComplications}
                        onChange={(e) => setAirwayComplications(e.target.value)}
                      >
                        <option value="">Select complication</option>
                        <option value="None">None</option>
                        <option value="Bronchial Stenosis">Bronchial Stenosis</option>
                        <option value="Anastomotic Dehiscence">Anastomotic Dehiscence</option>
                        <option value="Bronchomalacia">Bronchomalacia</option>
                        <option value="Excessive Granulation Tissue">Excessive Granulation Tissue</option>
                        <option value="Airway Infection">Airway Infection</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="rejection-events" className="text-muted-foreground">
                        Rejection Events
                      </Label>
                      <select
                        id="rejection-events"
                        className="w-full p-3 border border-border rounded-xl bg-background"
                        value={rejectionEvents}
                        onChange={(e) => setRejectionEvents(e.target.value)}
                      >
                        <option value="">Select rejection event</option>
                        <option value="None">None</option>
                        <option value="Acute Cellular Rejection (ACR) - Grade A1">Acute Cellular Rejection (ACR) - Grade A1</option>
                        <option value="Acute Cellular Rejection (ACR) - Grade A2">Acute Cellular Rejection (ACR) - Grade A2</option>
                        <option value="Acute Cellular Rejection (ACR) - Grade A3">Acute Cellular Rejection (ACR) - Grade A3</option>
                        <option value="Acute Cellular Rejection (ACR) - Grade A4">Acute Cellular Rejection (ACR) - Grade A4</option>
                        <option value="Antibody-Mediated Rejection (AMR)">Antibody-Mediated Rejection (AMR)</option>
                        <option value="Chronic Lung Allograft Dysfunction (CLAD)">Chronic Lung Allograft Dysfunction (CLAD)</option>
                        <option value="Bronchiolitis Obliterans Syndrome (BOS)">Bronchiolitis Obliterans Syndrome (BOS)</option>
                        <option value="Restrictive Allograft Syndrome (RAS)">Restrictive Allograft Syndrome (RAS)</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button 
                      variant="outline"
                      onClick={() => {
                        setClinicalNoteText('');
                        setAirwayComplications('');
                        setRejectionEvents('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button 
                      className="bg-accent hover:bg-accent/90"
                      onClick={() => {
                        if (!clinicalNoteText.trim()) {
                          toast.error('Please enter a note');
                          return;
                        }
                        if (!patientId) {
                          toast.error('No patient selected');
                          return;
                        }
                        
                        createNoteMutation.mutate({
                          patient_id: patientId,
                          notes: clinicalNoteText,
                          airway_complications: airwayComplications || null,
                          rejection_events: rejectionEvents || null
                        }, {
                          onSuccess: () => {
                            setClinicalNoteText('');
                            setAirwayComplications('');
                            setRejectionEvents('');
                          }
                        });
                      }}
                      disabled={createNoteMutation.isPending}
                    >
                      {createNoteMutation.isPending ? 'Saving...' : 'Save Note'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="text-primary">Previous Notes</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {clinicalNotesData?.notes && clinicalNotesData.notes.length > 0 ? (
                    clinicalNotesData.notes.map((note) => (
                      <div key={note.id} className="p-4 border border-border rounded-xl">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-primary">Dr. {note.doctor_name}</p>
                          <div className="flex items-center gap-2">
                            <p className="text-muted-foreground text-sm">
                              {new Date(note.created_at).toLocaleString('en-US', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false
                              })}
                            </p>
                            {user?.id === note.created_by && (
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setEditingNoteId(note.id);
                                    setEditingNoteText(note.notes);
                                    setEditingAirwayComplications(note.airway_complications || '');
                                    setEditingRejectionEvents(note.rejection_events || '');
                                  }}
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    if (confirm('Are you sure you want to delete this note?')) {
                                      deleteNoteMutation.mutate({
                                        noteId: note.id,
                                        patientId: patientId
                                      });
                                    }
                                  }}
                                >
                                  <X className="h-4 w-4 text-destructive" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                        {editingNoteId === note.id ? (
                          <div className="space-y-3">
                            <div className="space-y-2">
                              <Label className="text-muted-foreground text-xs">Clinical Observations</Label>
                              <textarea
                                className="w-full p-3 border border-border rounded-lg min-h-24"
                                value={editingNoteText}
                                onChange={(e) => setEditingNoteText(e.target.value)}
                              />
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div className="space-y-2">
                                <Label className="text-muted-foreground text-xs">Airway Complications</Label>
                                <select
                                  className="w-full p-2 border border-border rounded-lg bg-background text-sm"
                                  value={editingAirwayComplications}
                                  onChange={(e) => setEditingAirwayComplications(e.target.value)}
                                >
                                  <option value="">Select complication</option>
                                  <option value="None">None</option>
                                  <option value="Bronchial Stenosis">Bronchial Stenosis</option>
                                  <option value="Anastomotic Dehiscence">Anastomotic Dehiscence</option>
                                  <option value="Bronchomalacia">Bronchomalacia</option>
                                  <option value="Excessive Granulation Tissue">Excessive Granulation Tissue</option>
                                  <option value="Airway Infection">Airway Infection</option>
                                  <option value="Other">Other</option>
                                </select>
                              </div>
                              
                              <div className="space-y-2">
                                <Label className="text-muted-foreground text-xs">Rejection Events</Label>
                                <select
                                  className="w-full p-2 border border-border rounded-lg bg-background text-sm"
                                  value={editingRejectionEvents}
                                  onChange={(e) => setEditingRejectionEvents(e.target.value)}
                                >
                                  <option value="">Select rejection event</option>
                                  <option value="None">None</option>
                                  <option value="Acute Cellular Rejection (ACR) - Grade A1">Acute Cellular Rejection (ACR) - Grade A1</option>
                                  <option value="Acute Cellular Rejection (ACR) - Grade A2">Acute Cellular Rejection (ACR) - Grade A2</option>
                                  <option value="Acute Cellular Rejection (ACR) - Grade A3">Acute Cellular Rejection (ACR) - Grade A3</option>
                                  <option value="Acute Cellular Rejection (ACR) - Grade A4">Acute Cellular Rejection (ACR) - Grade A4</option>
                                  <option value="Antibody-Mediated Rejection (AMR)">Antibody-Mediated Rejection (AMR)</option>
                                  <option value="Chronic Lung Allograft Dysfunction (CLAD)">Chronic Lung Allograft Dysfunction (CLAD)</option>
                                  <option value="Bronchiolitis Obliterans Syndrome (BOS)">Bronchiolitis Obliterans Syndrome (BOS)</option>
                                  <option value="Restrictive Allograft Syndrome (RAS)">Restrictive Allograft Syndrome (RAS)</option>
                                  <option value="Other">Other</option>
                                </select>
                              </div>
                            </div>
                            
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setEditingNoteId(null);
                                  setEditingNoteText('');
                                  setEditingAirwayComplications('');
                                  setEditingRejectionEvents('');
                                }}
                              >
                                Cancel
                              </Button>
                              <Button
                                size="sm"
                                className="bg-accent hover:bg-accent/90"
                                onClick={() => {
                                  if (!editingNoteText.trim()) {
                                    toast.error('Note cannot be empty');
                                    return;
                                  }
                                  updateNoteMutation.mutate({
                                    noteId: note.id,
                                    data: { 
                                      notes: editingNoteText,
                                      airway_complications: editingAirwayComplications || null,
                                      rejection_events: editingRejectionEvents || null
                                    },
                                    patientId: patientId
                                  }, {
                                    onSuccess: () => {
                                      setEditingNoteId(null);
                                      setEditingNoteText('');
                                      setEditingAirwayComplications('');
                                      setEditingRejectionEvents('');
                                    }
                                  });
                                }}
                                disabled={updateNoteMutation.isPending}
                              >
                                {updateNoteMutation.isPending ? 'Saving...' : 'Save'}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <div>
                              <p className="text-foreground whitespace-pre-wrap">{note.notes}</p>
                            </div>
                            
                            {(note.airway_complications || note.rejection_events) && (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t border-border">
                                {note.airway_complications && (
                                  <div className="space-y-1">
                                    <p className="text-xs text-muted-foreground">Airway Complications</p>
                                    <Badge variant="outline" className="bg-[#F07D13]/10 border-[#F07D13]/30 text-[#F07D13]">
                                      {note.airway_complications}
                                    </Badge>
                                  </div>
                                )}
                                
                                {note.rejection_events && (
                                  <div className="space-y-1">
                                    <p className="text-xs text-muted-foreground">Rejection Events</p>
                                    <Badge variant="outline" className="bg-destructive/10 border-destructive/30 text-destructive">
                                      {note.rejection_events}
                                    </Badge>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        {note.updated_at && (
                          <p className="text-xs text-muted-foreground mt-2">
                            Last updated: {new Date(note.updated_at).toLocaleString()}
                          </p>
                        )}
                      </div>
                    ))
                  ) : (
                    <EmptyState
                      title="No clinical notes yet"
                      description="Add your first clinical note above to start tracking patient progress."
                      icon={FileText}
                    />
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Medications Tab */}
            <TabsContent value="medications" className="space-y-6">
              {/* Add/Edit Medication Form */}
              { showAddMedicationForm && (
                <Card className="shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-primary">
                      {editingMedication ? 'Edit Medication' : 'Add New Medication'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="medication-name" className="text-muted-foreground">
                          Medication Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="medication-name"
                          placeholder="e.g., Tacrolimus"
                          value={medicationForm.name}
                          onChange={(e) => setMedicationForm({ ...medicationForm, name: e.target.value })}
                          className="border-primary/30 focus:border-primary"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="medication-dosage" className="text-muted-foreground">
                          Dosage <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="medication-dosage"
                          placeholder="e.g., 2.5 mg"
                          value={medicationForm.dosage}
                          onChange={(e) => setMedicationForm({ ...medicationForm, dosage: e.target.value })}
                          className="border-primary/30 focus:border-primary"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="medication-frequency" className="text-muted-foreground">
                          Frequency <span className="text-red-500">*</span>
                        </Label>
  
                        <select
                          id="medication-frequency"
                          value={medicationForm.frequency}
                          onChange={(e) =>
                            setMedicationForm({ ...medicationForm, frequency: e.target.value })
                          }
                          className="w-full border border-primary/30 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          <option value="">Select frequency</option>
                          <option value="once_daily">Once Daily</option>
                          <option value="twice_daily">Twice Daily</option>
                          <option value="three_times_daily">Three Times Daily</option>
                          <option value="four_times_daily">Four Times Daily</option>
                          <option value="as_needed">As Needed</option>
                        </select>
                      </div>
  
                      <div className="space-y-2">
                        <Label htmlFor="medication-frequency" className="text-muted-foreground">
                          Scheduled Times <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="scheduled-times"
                          placeholder="2 PM, 8PM"
                          value={medicationForm.scheduled_times}
                          onChange={(e) => setMedicationForm({ ...medicationForm, scheduled_times: e.target.value })}
                          className="border-primary/30 focus:border-primary"
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="medication-notes" className="text-muted-foreground">
                          Notes (Optional)
                        </Label>
                        <textarea
                          id="medication-notes"
                          placeholder="e.g., Take with food. Monitor blood levels regularly."
                          value={medicationForm.notes}
                          onChange={(e) => setMedicationForm({ ...medicationForm, notes: e.target.value })}
                          className="w-full p-3 border border-primary/30 focus:border-primary rounded-xl min-h-20"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <Button 
                        variant="outline"
                        onClick={resetMedicationForm}
                      >
                        Cancel
                      </Button>
                      <Button 
                        className="bg-primary hover:bg-primary/90"
                        onClick={editingMedication ? handleUpdateMedication : handleAddMedication}
                      >
                        {editingMedication ? 'Update Medication' : 'Add Medication'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
  
              {/* Medications List */}
              <Card className="shadow-lg">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-primary">Current Medications</CardTitle>
                    {!showAddMedicationForm  && (
                      <Button 
                        className="bg-primary hover:bg-primary/90"
                        onClick={() => setShowAddMedicationForm(true)}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Medication
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {medications.length === 0 ? (
                    <div className="text-center py-12">
                      <Pill className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
                      <h3 className="text-lg text-muted-foreground mb-2">No Medications</h3>
                      <p className="text-muted-foreground mb-4">
                        Start by adding medications for this patient.
                      </p>
                      <Button 
                        className="bg-primary hover:bg-primary/90"
                        onClick={() => setShowAddMedicationForm(true)}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add First Medication
                      </Button>
                    </div>
                  ) : (
                    <div className="overflow-x-auto -mx-4 sm:mx-0">
                      <div className="inline-block min-w-full align-middle">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Medication Name</TableHead>
                              <TableHead>Dosage</TableHead>
                              <TableHead>Frequency</TableHead> 
                              <TableHead>Scheduled Times</TableHead>                            
                            <TableHead>Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {medications.map((medication) => (
                              <TableRow key={medication.id}>
                                <TableCell>
                                  <div>
                                    <p className="font-medium text-primary">{medication.name}</p>
                                    {medication.notes && (
                                      <p className="text-xs text-muted-foreground mt-1">
                                        {medication.notes}
                                      </p>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>{medication.dosage}</TableCell>
                                <TableCell>{medication.frequency}</TableCell>
                                <TableCell>{medication.scheduled_times}</TableCell>                                
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleEditMedication(medication)}
                                      className="text-primary hover:text-primary/80"
                                    >
                                      <Pencil className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDeleteMedication(medication.id)}
                                      className="text-red-500 hover:text-red-600"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>  
          </Tabs>
        </div>
      </div>
    );
  }

  // Main Dashboard View
  return (
    <div className="min-h-screen bg-background">
      {/* Top Navigation */}
      <div className="bg-white border-b border-border sticky top-0 z-40 shadow-sm">
        <div className="px-4 md:px-6 lg:px-8">
          {/* Doctor Info Header */}
          <div className="py-4 border-b border-border">
            <div className="flex items-center justify-between">
              <div>
                <h1>Welcome, {doctor.name}</h1>
                <p className="text-muted-foreground">{doctor.specialty}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-primary/10 text-primary border-primary">
                  {totalPatients} Patients
                </Badge>
                {activeAlerts > 0 && (
                  <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive">
                    {activeAlerts} Alerts
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex gap-1 overflow-x-auto py-2">
            <button 
              onClick={() => setActiveSection('overview')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap ${
                activeSection === 'overview' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'text-foreground hover:bg-[#e5e5e3]'
              }`}
            >
              <BarChart3 className="w-4 h-4" />
              <span className="hidden sm:inline">Overview</span>
            </button>
            <button 
              onClick={() => setActiveSection('patients')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap ${
                activeSection === 'patients' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'text-foreground hover:bg-[#e5e5e3]'
              }`}
            >
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Patient List</span>
            </button>
            <button 
              onClick={() => setActiveSection('alerts')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap relative ${
                activeSection === 'alerts' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'text-foreground hover:bg-[#e5e5e3]'
              }`}
            >
              <AlertTriangle className="w-4 h-4" />
              <span className="hidden sm:inline">Alerts</span>
              {activeAlerts > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#F07D13] text-white rounded-full flex items-center justify-center text-xs">
                  {activeAlerts}
                </span>
              )}
            </button>
            <button 
              onClick={() => setActiveSection('symptoms')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap relative ${
                activeSection === 'symptoms' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'text-foreground hover:bg-[#e5e5e3]'
              }`}
            >
              <MessageSquare className="w-4 h-4" />
              <span className="hidden sm:inline">Symptoms</span>
              {symptomPatients.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#F07D13] text-white rounded-full flex items-center justify-center text-xs">
                  {symptomPatients.length}
                </span>
              )}
            </button>
            <button 
              onClick={() => setActiveSection('low-adherence')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap relative ${
                activeSection === 'low-adherence' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'text-foreground hover:bg-[#e5e5e3]'
              }`}
            >
              <TrendingDown className="w-4 h-4" />
              <span className="hidden sm:inline">Low Adherence</span>
              {lowAdherencePatients.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#F07D13] text-white rounded-full flex items-center justify-center text-xs">
                  {lowAdherencePatients.length}
                </span>
              )}
            </button>
            <button 
              onClick={() => onNavigate?.('messaging')}
              className="ml-2 flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap relative bg-[#14CAF4] text-white hover:bg-[#14CAF4]/90"
            >
              <MessageCircle className="w-4 h-4" />
              <span className="hidden sm:inline">Chat</span>
              {unreadMessagesCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#F07D13] text-white rounded-full flex items-center justify-center text-xs">
                  {unreadMessagesCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 md:px-6 lg:px-8 py-6">
        {/* Overview Section - Stats Cards */}
        {activeSection === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 mb-8">
            {/* Total Patients with Pre-Op/Post-Op Breakdown */}
            <Card 
              className="cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => setActiveSection('patients')}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-muted-foreground text-sm">Total Patients</p>
                    <h2 className="text-primary text-3xl">{totalPatients}</h2>
                  </div>
                  <Users className="w-8 h-8 text-primary" />
                </div>
                <div className="flex gap-4 mt-2 pt-3 border-t border-border/50">
                  <div className="flex-1">
                    <div className="text-xs text-muted-foreground">Pre-Op</div>
                    <div className="text-lg text-blue-600">
                      {dashboardData?.pre_op_count || 0}
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="text-xs text-muted-foreground">Post-Op</div>
                    <div className="text-lg text-green-600">
                      {dashboardData?.post_op_count || 0}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card 
              className="cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => onNavigate?.('appointments')}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground text-sm">Appointments</p>
                    <h2 className="text-[#14CAF4] text-3xl">{totalAppointmentsCount}</h2>
                    <p className="text-muted-foreground text-xs mt-1">Today: {todaysAppointmentsCount}</p>
                  </div>
                  <Calendar className="w-8 h-8 text-[#14CAF4]" />
                </div>
              </CardContent>
            </Card>

            <Card 
              className="cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => setActiveSection('low-adherence')}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground text-sm">Avg Adherence</p>
                    <h2 className="text-[#14CAF4] text-3xl">{avgAdherence}%</h2>
                  </div>
                  <TrendingUp className="w-8 h-8 text-[#14CAF4]" />
                </div>
              </CardContent>
            </Card>

            {/* Lab Notifications OP - NEW TILE */}
            <Card 
              className="cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => setActiveSection('alerts')}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground text-xs">Lab Notifications OP</p>
                    <h2 className="text-accent text-3xl">{dashboardData?.lab_notifications_op || 0}</h2>
                    <p className="text-xs text-muted-foreground mt-1">Pending review</p>
                  </div>
                  <FlaskConical className="w-8 h-8 text-accent" />
                </div>
              </CardContent>
            </Card>

            {/* Active Alerts with IP/OP & High-Risk Breakdown */}
            <Card 
              className="cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => setActiveSection('alerts')}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex-1">
                    <p className="text-muted-foreground text-sm">Active Alerts</p>
                    <h2 className="text-[#F07D13] text-3xl">{activeAlerts}</h2>
                    <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                      <span>IP: <strong className="text-foreground">{dashboardData?.active_alerts_ip || 0}</strong></span>
                      <span>OP: <strong className="text-foreground">{dashboardData?.active_alerts_op || 0}</strong></span>
                    </div>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-[#F07D13]" />
                </div>
                <div className="border-t border-border/50 pt-2 mt-2">
                  <div className="text-xs text-destructive">High-Risk Alerts</div>
                  <div className="text-lg text-destructive">{dashboardData?.high_risk_alerts_total || 0}</div>
                  <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                    <span>IP: <strong className="text-destructive">{dashboardData?.high_risk_alerts_ip || 0}</strong></span>
                    <span>OP: <strong className="text-destructive">{dashboardData?.high_risk_alerts_op || 0}</strong></span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card 
              className="cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => setActiveSection('symptoms')}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground text-sm">Symptoms</p>
                    <h2 className="text-[#F07D13] text-3xl">{symptomPatients.length}</h2>
                  </div>
                  <Stethoscope className="w-8 h-8 text-[#F07D13]" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Today's Appointments Section in Overview */}
        {activeSection === "overview" &&
          (() => {
            const todaysAppointments = dashboardData?.today_appointments || dashboardData?.appointments_today || [];
            

            return (
              <Card className="shadow-lg mb-8">
                <CardHeader>
                  <CardTitle className="text-primary flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Today's Appointments (
                    {todaysAppointments.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {todaysAppointments.map((apt:any) => (
                      <div
                        key={apt.id}
                        className="flex flex-col p-4 bg-[#d4f0f8] rounded-lg hover:shadow-md transition-shadow border border-[#14CAF4]/20"
                      >
                        <div className="flex items-start gap-4">
                          {/* Patient Avatar */}
                          <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                            <span className="text-primary-foreground">
                              {apt.patient_name
                                .split(" ")
                                .map((n) => n[0])
                                .join("")}
                            </span>
                          </div>

                          {/* Patient Details */}
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                              <div>
                                <p className="text-foreground font-medium">
                                  {apt.patient_name}
                                </p>
                                <p className="text-muted-foreground text-sm">
                                  {apt.umrNo} • {apt.age} years                                  
                                </p>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <Badge
                                  variant="outline"
                                  className={`
                                ${
                                  apt.type === "Emergency"
                                    ? "bg-[#F07D13]/10 text-[#F07D13] border-[#F07D13]"
                                    : apt.type === "Follow-up"
                                      ? "bg-primary/10 text-primary border-primary"
                                      : "bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]"
                                }
                              `}
                                >
                                  {apt.type}
                                </Badge>
                                <Badge
                                  variant="outline"
                                  className="bg-[#e5e5e3]"
                                >
                                  {apt.time}
                                </Badge>
                              </div>
                            </div>

                            {/* Reason for Review */}
                            <div className="p-3 bg-white rounded-lg border border-border mb-2">
                              <p className="text-[#494949] text-xs mb-1">
                                Reason for Review:
                              </p>
                              <p className="text-foreground text-sm">
                                {apt.reason}
                              </p>
                            </div>
                          </div>

                          {/* Action Button */}
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-shrink-0"
                            onClick={() => handleJoinCall(apt)}
                          >
                            <Video className="w-4 h-4 mr-2" />
                            Start
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })()}

        {/* Patient List Section */}
        {activeSection === 'patients' && (
          <div className="space-y-6">
            {/* Search and Filters */}
              <Card className="shadow-lg">
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                      <Input 
                        placeholder="Search patients by name..." 
                        className="pl-10" 
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                    <Select value={adherenceFilter} onValueChange={setAdherenceFilter}>
                      <SelectTrigger className="w-full md:w-48">
                        <SelectValue placeholder="Filter by Adherence" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Adherence</SelectItem>
                        <SelectItem value="high">High (&gt;85%)</SelectItem>
                        <SelectItem value="medium">Medium (70-85%)</SelectItem>
                        <SelectItem value="low">Low (&lt;70%)</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-full md:w-48">
                        <SelectValue placeholder="Filter by Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="On Track">On Track</SelectItem>
                        <SelectItem value="Missed">Missed</SelectItem>
                        <SelectItem value="Escalated">Escalated</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {/* Patient Table */}
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="text-primary">
                    Patient Overview ({filteredPatients.length} patients)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-left">Patient Name</TableHead>
                        <TableHead className="text-center">Age</TableHead>
                        <TableHead className="text-center">Post Tx Duration</TableHead>
                        <TableHead className="text-center">Adherence</TableHead>
                        <TableHead className="text-center">Last FEV1</TableHead>
                        <TableHead className="text-center">Last 6MWD</TableHead>
                        <TableHead className="text-center">Last Test</TableHead>
                        <TableHead className="text-center">CLAD</TableHead>
                        <TableHead className="text-center">Status</TableHead>
                        <TableHead className="text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredPatients.map((patient) => (
                        <TableRow key={patient.id}>
                          <TableCell className="text-left">{patient.name || 'Unknown'}</TableCell>
                          <TableCell className="text-center">{patient.age || 'N/A'}</TableCell>
                          <TableCell className="text-center">
                            <span className="text-sm">
                              {calculatePostTxDuration(patient.transplant_date)}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className={getAdherenceColor(patient.adherence)}>
                              {patient.adherence}%
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex flex-col items-center">
                              <p>{(patient.lastFEV1 || 0).toFixed(1)}%</p>
                              <div className="flex items-center gap-1">
                                {patient.fev1Change >= 0 ? (
                                  <TrendingUp className="w-3 h-3 text-[#14CAF4]" />
                                ) : (
                                  <TrendingDown className="w-3 h-3 text-[#F07D13]" />
                                )}
                                <span className={patient.fev1Change >= 0 ? 'text-[#14CAF4]' : 'text-[#F07D13]'}>
                                  {patient.fev1Change >= 0 ? '+' : ''}{(patient.fev1Change || 0).toFixed(1)}%
                                </span>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex flex-col items-center">
                              <p>{(patient.last6MWD || 0).toFixed(0)} m</p>
                              <div className="flex items-center gap-1">
                                {patient.sixMWDChange >= 0 ? (
                                  <TrendingUp className="w-3 h-3 text-[#14CAF4]" />
                                ) : (
                                  <TrendingDown className="w-3 h-3 text-[#F07D13]" />
                                )}
                                <span className={patient.sixMWDChange >= 0 ? 'text-[#14CAF4]' : 'text-[#F07D13]'}>
                                  {patient.sixMWDChange >= 0 ? '+' : ''}{(patient.sixMWDChange || 0).toFixed(0)} m
                                </span>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-center text-muted-foreground">{patient.lastSession}</TableCell>
                          <TableCell className="text-center">
                            {patient.clad_status === 'DIAGNOSED' && (
                              <Badge variant="destructive">Yes</Badge>
                            )}
                            {patient.clad_status === 'NEGATIVE' && (
                              <Badge className="bg-green-100 text-green-800 border-green-300">No</Badge>
                            )}
                            {patient.clad_status === 'UNDER_EVALUATION' && (
                              <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">Under Evaluation</Badge>
                            )}
                            {(!patient.clad_status || patient.clad_status === 'NOT_APPLICABLE') && (
                              <span className="text-muted-foreground">N/A</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline" className={getStatusColor(patient.status)}>
                              {patient.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex gap-2 justify-center">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-primary"
                                onClick={() => handleViewPatient(patient)}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                View Details
                              </Button>
                              <Button  
                                variant="ghost" 
                                size="sm" 
                                className="text-[#14CAF4]"
                                onClick={() => handleViewHomeNurseTasks(patient)}
                              >
                                <Home className="w-4 h-4 mr-1" />
                                Home Nurse Tasks
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
          </div>
        )}

        {/* Alerts Section */}
        {activeSection === 'alerts' && (
          <div className="space-y-6">
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="text-[#F07D13] flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Critical Alerts ({alertPatients.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {alertPatients.map((patient) => (
                    <Card key={patient.id} className="border-2 border-[#F07D13]">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="text-primary">{patient.name || 'Unknown Patient'}</h3>
                            <p className="text-muted-foreground">{patient.age || 'N/A'} years old</p>
                          </div>
                          <Badge variant="outline" className="bg-[#F07D13]/10 text-[#F07D13] border-[#F07D13]">
                            {patient.alertType}
                          </Badge>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                          <div className="p-3 bg-[#e5e5e3] rounded-lg">
                            <p className="text-muted-foreground">FEV1</p>
                            <div className="flex items-center gap-1">
                              <p className="text-primary">{patient.lastFEV1?.toFixed(1) || 'N/A'}%</p>
                              <TrendingDown className="w-4 h-4 text-[#F07D13]" />
                            </div>
                          </div>
                          <div className="p-3 bg-[#e5e5e3] rounded-lg">
                            <p className="text-muted-foreground">6MWD</p>
                            <div className="flex items-center gap-1">
                              <p className="text-primary">{patient.last6MWD?.toFixed(0) || 'N/A'} m</p>
                              {(patient.sixMWDChange || 0) < 0 && <TrendingDown className="w-4 h-4 text-[#F07D13]" />}
                            </div>
                          </div>
                          <div className="p-3 bg-[#e5e5e3] rounded-lg">
                            <p className="text-muted-foreground">SpO2</p>
                            <p className="text-primary">{patient.lastSpO2?.toFixed(0) || 'N/A'}%</p>
                          </div>
                          <div className="p-3 bg-[#e5e5e3] rounded-lg">
                            <p className="text-muted-foreground">Adherence</p>
                            <p className={getAdherenceColor(patient.adherence)}>{patient.adherence}%</p>
                          </div>
                        </div>

                        <div className="p-3 bg-[#feebda] rounded-lg mb-4">
                          <p className="text-[#F07D13]">
                            <AlertCircle className="w-4 h-4 inline mr-2" />
                            {patient.alertType === 'FEV1 Drop' && 'FEV1 dropped by >10% from baseline. Immediate review recommended.'}
                            {patient.alertType === '6MWD Drop' && '6MWD decreased by >30m from last test. Investigate cause.'}
                            {patient.alertType === 'Low SpO2' && 'SpO2 below 95% during recent tests. Monitor closely.'}
                            {patient.alertType === 'Lab Alert' && 'Abnormal lab values detected. Review results immediately.'}
                          </p>
                        </div>

                        <div className="flex gap-2">
                          <Button 
                            className="flex-1 bg-primary hover:bg-primary/90"
                            onClick={() => handleViewPatient(patient)}
                          >
                            View Patient Details
                          </Button>
                          <Button variant="outline" className="flex-1">
                            <MessageSquare className="w-4 h-4 mr-2" />
                            Contact Patient
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  {alertPatients.length === 0 && (
                    <div className="text-center py-12">
                      <CheckCircle className="w-16 h-16 text-[#14CAF4] mx-auto mb-4" />
                      <h3 className="text-primary mb-2">No Active Alerts</h3>
                      <p className="text-muted-foreground">
                        All patients are within normal parameters
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
          </div>
        )}

        {/* Symptoms and Alerts Section */}
        {activeSection === 'symptoms' && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6 mb-6">
              <Card className="shadow-lg border-l-4 border-l-destructive">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-destructive" />
                      <h3 className="text-destructive">Patients with Symptoms</h3>
                    </div>
                    <Badge variant="destructive" className="text-xs sm:text-sm">
                      {symptomPatients.length}
                    </Badge>
                  </div>
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    Patients reporting concerning symptoms requiring attention
                  </p>
                </CardContent>
              </Card>

              <Card className="shadow-lg border-l-4 border-l-accent">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-accent" />
                      <h3 className="text-accent">Alerts</h3>
                    </div>
                    <Badge className="bg-accent text-xs sm:text-sm">
                      {alertPatients.length}
                    </Badge>
                  </div>
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    Patients with vitals outside normal parameters
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Patients with Symptoms */}
            <Card className="shadow-lg mb-4 sm:mb-6">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-destructive text-base sm:text-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Patients with Symptoms
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-6">
                {symptomPatients.length === 0 ? (
                  <div className="text-center py-6 sm:py-8">
                    <Activity className="w-10 h-10 sm:w-12 sm:h-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground text-sm sm:text-base">
                      No patients with reported symptoms
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-3 sm:gap-4">
                    {symptomPatients.map((patient) => {
                      // Use actual symptom data from API
                      const symptom = patient.latest_symptom || patient.symptoms || 'Reported symptoms';
                      
                      return (
                        <div key={patient.id} className="p-3 sm:p-4 border border-destructive/30 rounded-xl bg-destructive/5">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-destructive flex items-center justify-center flex-shrink-0">
                                <span className="text-destructive-foreground text-sm sm:text-base">
                                  {patient.name?.split(' ').map(n => n[0]).join('') || '?'}
                                </span>
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm sm:text-base">{patient.name || 'Unknown Patient'}</p>
                                <p className="text-muted-foreground text-xs sm:text-sm truncate">
                                  {patient.cohort_name || patient.diagnosis || 'No cohort assigned'}
                                </p>
                                <div className="flex items-center gap-1 mt-1">
                                  <AlertTriangle className="w-3 h-3 text-destructive" />
                                  <p className="text-destructive text-xs">{symptom}</p>
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-1.5 sm:gap-2 flex-wrap">
                              <Button
                                size="sm"
                                onClick={() => {
                                  // Find the full patient object and view details
                                  const fullPatient = enhancedPatients.find(p => 
                                    p.id === (patient.patient_id || patient.id)
                                  );
                                  if (fullPatient) {
                                    handleViewPatient(fullPatient);
                                  }
                                }}
                                className="bg-[#14CAF4] hover:bg-[#14CAF4]/90 text-xs sm:text-sm px-2 sm:px-3 h-8"
                              >
                                <Eye className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                                <span className="hidden sm:inline">View</span>
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Patients with Alerts (Out of Range Vitals) */}
            <Card className="shadow-lg">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-accent text-base sm:text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Alerts - Out of Range Vitals
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-6">
                {alertPatients.length === 0 ? (
                  <div className="text-center py-6 sm:py-8">
                    <Heart className="w-10 h-10 sm:w-12 sm:h-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground text-sm sm:text-base">
                      All patient vitals are within normal range
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-3 sm:gap-4">
                    {alertPatients.map((patient) => {
                      // Use actual vitals data from API
                      const vitalIssue = patient.out_of_range_vital || patient.vital_alert || {
                        vital: 'Vital Alert',
                        value: 'N/A',
                        normal: 'See details'
                      };
                      
                      return (
                        <div key={patient.id} className="p-3 sm:p-4 border border-accent/30 rounded-xl bg-accent/5">
                          <div className="flex flex-col gap-3">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-accent flex items-center justify-center flex-shrink-0">
                                <span className="text-accent-foreground text-sm sm:text-base">
                                  {patient.name?.split(' ').map(n => n[0]).join('') || '?'}
                                </span>
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm sm:text-base">{patient.name || 'Unknown Patient'}</p>
                                <p className="text-muted-foreground text-xs sm:text-sm truncate">
                                  {patient.cohort_name || patient.diagnosis || 'No cohort assigned'}
                                </p>
                              </div>
                            </div>
                            
                            <div className="bg-white p-2 sm:p-3 rounded-lg">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-accent text-xs sm:text-sm">{vitalIssue.vital}</p>
                                  <p className="text-muted-foreground text-xs">Normal: {vitalIssue.normal}</p>
                                </div>
                                <p className="text-destructive">{vitalIssue.value}</p>
                              </div>
                            </div>

                            <div className="flex gap-1.5 sm:gap-2 flex-wrap">
                              <Button
                                size="sm"
                                onClick={() => {
                                  // Find the full patient object and view details
                                  const fullPatient = enhancedPatients.find(p => 
                                    p.id === (patient.patient_id || patient.id)
                                  );
                                  if (fullPatient) {
                                    handleViewPatient(fullPatient);
                                  }
                                }}
                                className="bg-[#14CAF4] hover:bg-[#14CAF4]/90 text-xs sm:text-sm px-2 sm:px-3 h-8"
                              >
                                <Eye className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                                <span className="hidden sm:inline">View</span>
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Reviews Section - Pending Sign-offs */}
        {activeSection === 'reviews' && (
          <div className="space-y-6">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-primary flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Pending Reviews
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* This section can be implemented later */}
                  <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 text-[#14CAF4] mx-auto mb-4" />
                    <h3 className="text-primary mb-2">All Caught Up!</h3>
                    <p className="text-muted-foreground">
                      No pending reviews at this time
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Symptoms Section - Unreviewed Patient Symptoms */}
        {activeSection === 'symptoms' && (
          <DoctorSymptomsReview />
        )}

        {/* Low Adherence Section */}
        {activeSection === 'low-adherence' && (
          <div className="space-y-6">
            <Card className="shadow-lg border-l-4 border-l-[#F07D13]">
              <CardHeader>
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <CardTitle className="text-[#F07D13] flex items-center gap-2">
                      <TrendingDown className="w-5 h-5" />
                      Low Adherence Dashboard ({lowAdherencePatients.length} Patients)
                    </CardTitle>
                    {lowAdherencePatients.length > 0 && (
                      <div className="flex gap-2">
                        <Badge variant="outline" className="bg-[#F07D13]/10 text-[#F07D13] border-[#F07D13]">
                          Needs Review: {lowAdherencePatients.length}
                        </Badge>
                      </div>
                    )}
                  </div>
                  <p className="text-muted-foreground">
                    Patients with adherence below 100%. Review each patient's adherence details, add clinical notes, and mark as reviewed to remove from this list.
                  </p>
                </div>
              </CardHeader>
              <CardContent>
                {lowAdherencePatients.length > 0 ? (
                  <div className="space-y-4">
                    {lowAdherencePatients
                      .sort((a, b) => a.adherence - b.adherence)
                      .map((patient) => {
                        const adherenceDetail = getPatientAdherenceDetail(patient);
                        return (
                          <Card key={patient.id} className="border-2 border-[#F07D13]/30 hover:shadow-md transition-shadow">
                            <CardContent className="p-4 sm:p-6">
                              {/* Patient Header */}
                              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-4">
                                <div className="flex items-start gap-3 sm:gap-4 flex-1">
                                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                                    <span className="text-primary-foreground">
                                      {patient.name?.split(' ').map(n => n[0]).join('') || '?'}
                                    </span>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h3 className="text-primary mb-1">{patient.name || 'Unknown Patient'}</h3>
                                    <p className="text-muted-foreground mb-2">
                                      {patient.age || 'N/A'} years • {patient.cohort_name || 'No Cohort'}
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                      <Badge variant="outline" className={getStatusColor(patient.status)}>
                                        {patient.status}
                                      </Badge>
                                      <Badge variant="outline" className="bg-[#F07D13]/10 text-[#F07D13] border-[#F07D13]">
                                        {patient.adherence}% Overall
                                      </Badge>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Adherence Breakdown by Category */}
                              <div className="mb-4">
                                <h4 className="text-primary mb-3">Adherence Breakdown</h4>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                  {/* Medication Adherence */}
                                  <div className={`p-4 rounded-xl border-2 ${
                                    adherenceDetail.medication.overall < 85 
                                      ? 'bg-[#FEEBDA] border-[#F07D13]/30' 
                                      : 'bg-[#D4F0F8] border-[#14CAF4]/30'
                                  }`}>
                                    <div className="flex items-center justify-between mb-2">
                                      <Pill className={`w-5 h-5 ${
                                        adherenceDetail.medication.overall < 85 ? 'text-[#F07D13]' : 'text-[#14CAF4]'
                                      }`} />
                                      <span className={`${
                                        adherenceDetail.medication.overall < 85 ? 'text-[#F07D13]' : 'text-[#14CAF4]'
                                      }`}>
                                        {adherenceDetail.medication.overall}%
                                      </span>
                                    </div>
                                    <p className="text-sm text-foreground">Medication</p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                      {adherenceDetail.medication.takenDoses}/{adherenceDetail.medication.scheduledDoses} doses
                                    </p>
                                  </div>

                                  {/* Exercise Adherence */}
                                  <div className={`p-4 rounded-xl border-2 ${
                                    adherenceDetail.exercise.overall < 85 
                                      ? 'bg-[#FEEBDA] border-[#F07D13]/30' 
                                      : 'bg-[#D4F0F8] border-[#14CAF4]/30'
                                  }`}>
                                    <div className="flex items-center justify-between mb-2">
                                      <Activity className={`w-5 h-5 ${
                                        adherenceDetail.exercise.overall < 85 ? 'text-[#F07D13]' : 'text-[#14CAF4]'
                                      }`} />
                                      <span className={`${
                                        adherenceDetail.exercise.overall < 85 ? 'text-[#F07D13]' : 'text-[#14CAF4]'
                                      }`}>
                                        {adherenceDetail.exercise.overall}%
                                      </span>
                                    </div>
                                    <p className="text-sm text-foreground">Exercise</p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                      {adherenceDetail.exercise.completedExercises}/{adherenceDetail.exercise.assignedExercises} sessions
                                    </p>
                                  </div>

                                  {/* Home Test Adherence */}
                                  <div className={`p-4 rounded-xl border-2 ${
                                    adherenceDetail.homeTest.overall < 85 
                                      ? 'bg-[#FEEBDA] border-[#F07D13]/30' 
                                      : 'bg-[#D4F0F8] border-[#14CAF4]/30'
                                  }`}>
                                    <div className="flex items-center justify-between mb-2">
                                      <FlaskConical className={`w-5 h-5 ${
                                        adherenceDetail.homeTest.overall < 85 ? 'text-[#F07D13]' : 'text-[#14CAF4]'
                                      }`} />
                                      <span className={`${
                                        adherenceDetail.homeTest.overall < 85 ? 'text-[#F07D13]' : 'text-[#14CAF4]'
                                      }`}>
                                        {adherenceDetail.homeTest.overall}%
                                      </span>
                                    </div>
                                    <p className="text-sm text-foreground">Home Tests</p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                      {adherenceDetail.homeTest.completedTests}/{adherenceDetail.homeTest.scheduledTests} tests
                                    </p>
                                  </div>
                                </div>
                              </div>

                              {/* Clinical Indicators */}
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                                <div className="p-3 bg-[#e5e5e3] rounded-lg">
                                  <p className="text-xs text-muted-foreground">Last Session</p>
                                  <p className="text-sm text-primary">{patient.lastSession}</p>
                                </div>
                                <div className="p-3 bg-[#e5e5e3] rounded-lg">
                                  <p className="text-xs text-muted-foreground">FEV1</p>
                                  <div className="flex items-center gap-1">
                                    <p className="text-sm text-primary">{patient.lastFEV1?.toFixed(1) || 'N/A'}%</p>
                                    {(patient.fev1Change || 0) >= 0 ? (
                                      <TrendingUp className="w-3 h-3 text-[#14CAF4]" />
                                    ) : (
                                      <TrendingDown className="w-3 h-3 text-[#F07D13]" />
                                    )}
                                  </div>
                                </div>
                                <div className="p-3 bg-[#e5e5e3] rounded-lg">
                                  <p className="text-xs text-muted-foreground">6MWD</p>
                                  <div className="flex items-center gap-1">
                                    <p className="text-sm text-primary">{patient.last6MWD?.toFixed(0) || 'N/A'} m</p>
                                    {(patient.sixMWDChange || 0) >= 0 ? (
                                      <TrendingUp className="w-3 h-3 text-[#14CAF4]" />
                                    ) : (
                                      <TrendingDown className="w-3 h-3 text-[#F07D13]" />
                                    )}
                                  </div>
                                </div>
                                <div className="p-3 bg-[#e5e5e3] rounded-lg">
                                  <p className="text-xs text-muted-foreground">SpO2</p>
                                  <p className="text-sm text-primary">{patient.lastSpO2?.toFixed(0) || 'N/A'}%</p>
                                </div>
                              </div>

                              {/* Adherence Severity Alert */}
                              <div className={`p-3 rounded-lg mb-4 ${
                                patient.adherence < 50 
                                  ? 'bg-[#F07D13]/10 border border-[#F07D13]' 
                                  : patient.adherence < 70
                                  ? 'bg-[#FEEBDA]'
                                  : 'bg-[#D4F0F8]'
                              }`}>
                                <div className="flex items-start gap-2">
                                  <AlertCircle className={`w-4 h-4 mt-0.5 ${
                                    patient.adherence < 70 ? 'text-[#F07D13]' : 'text-[#2762AC]'
                                  }`} />
                                  <div className="flex-1">
                                    <p className="text-sm">
                                      {patient.adherence < 50 
                                        ? '🚨 Critical: Immediate intervention recommended. Consider scheduling urgent follow-up.'
                                        : patient.adherence < 70
                                        ? '⚠️ Low adherence detected. Patient may need support. Review detailed breakdown.'
                                        : patient.adherence < 85
                                        ? '📊 Moderate adherence. Encourage patient to maintain progress.'
                                        : '✓ Good adherence. Minor improvements needed for optimal compliance.'}
                                    </p>
                                  </div>
                                </div>
                              </div>

                              {/* Action Buttons */}
                              <div className="flex flex-col sm:flex-row gap-2">
                                <Button 
                                  size="sm"
                                  className="flex-1 bg-primary hover:bg-primary/90"
                                  onClick={() => handleViewAdherenceDetail(patient)}
                                >
                                  <FileText className="w-4 h-4 mr-2" />
                                  View Adherence Details
                                </Button>
                                <Button 
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleViewPatient(patient)}
                                >
                                  <Eye className="w-4 h-4 mr-2" />
                                  <span className="hidden sm:inline">Clinical Profile</span>
                                  <span className="sm:hidden">Profile</span>
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 text-[#14CAF4] mx-auto mb-4" />
                    <h3 className="text-primary mb-2">Excellent Adherence Across All Patients</h3>
                    <p className="text-muted-foreground">
                      All patients are maintaining 100% adherence to their rehabilitation programs
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Summary Statistics */}
            {lowAdherencePatients.length > 0 && (
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="text-primary">Low Adherence Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-[#FEEBDA] rounded-xl">
                      <p className="text-muted-foreground mb-1">Total Patients</p>
                      <h2 className="text-[#F07D13]">{lowAdherencePatients.length}</h2>
                      <p className="text-xs text-muted-foreground mt-1">Need review</p>
                    </div>
                    <div className="p-4 bg-[#e5e5e3] rounded-xl">
                      <p className="text-muted-foreground mb-1">Critical (&lt;50%)</p>
                      <h2 className="text-[#F07D13]">
                        {lowAdherencePatients.filter(p => p.adherence < 50).length}
                      </h2>
                      <p className="text-xs text-muted-foreground mt-1">Urgent action</p>
                    </div>
                    <div className="p-4 bg-[#e5e5e3] rounded-xl">
                      <p className="text-muted-foreground mb-1">Low (50-70%)</p>
                      <h2 className="text-[#F07D13]">
                        {lowAdherencePatients.filter(p => p.adherence >= 50 && p.adherence < 70).length}
                      </h2>
                      <p className="text-xs text-muted-foreground mt-1">Needs support</p>
                    </div>
                    <div className="p-4 bg-[#e5e5e3] rounded-xl">
                      <p className="text-muted-foreground mb-1">Moderate (70-100%)</p>
                      <h2 className="text-primary">
                        {lowAdherencePatients.filter(p => p.adherence >= 70).length}
                      </h2>
                      <p className="text-xs text-muted-foreground mt-1">On track</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Patient Onboarding Section */}
        {activeSection === 'onboarding' && (
          <div className="space-y-6">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-primary flex items-center gap-2">
                  <UserPlus className="w-5 h-5" />
                  New Patients - Discharge Summary Entry
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(dashboardData?.new_patients_pending_onboarding || []).filter(p => !completedOnboarding.includes(p.patient_id || p.id)).length > 0 ? (
                  <div className="space-y-4">
                    {(dashboardData?.new_patients_pending_onboarding || [])
                      .filter(p => !completedOnboarding.includes(p.patient_id || p.id))
                      .map((patient) => {
                        const getDaysAgo = (dateString: string) => {
                          const date = new Date(dateString);
                          const today = new Date('2025-10-19');
                          const diffTime = Math.abs(today.getTime() - date.getTime());
                          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                          return diffDays;
                        };

                        return (
                          <Card key={patient.id} className="border-2 border-accent/30">
                            <CardContent className="p-6">
                              <div className="flex items-center justify-between gap-4">
                                <div className="flex items-start gap-4 flex-1">
                                  <div className="w-14 h-14 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                                    <span className="text-primary-foreground">
                                      {patient.name?.split(' ').map(n => n[0]).join('') || '?'}
                                    </span>
                                  </div>
                                  <div className="flex-1">
                                    <h3 className="text-primary mb-1">{patient.name || 'Unknown Patient'}</h3>
                                    <p className="text-muted-foreground mb-2">
                                      {patient.age || 'N/A'} years • {patient.cohort_name || 'No Cohort'}
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                      <Badge variant="outline" className="bg-secondary">
                                        {patient.umr_no}
                                      </Badge>
                                      <Badge variant="outline" className="bg-accent/10 text-accent border-accent">
                                        Admitted {getDaysAgo(patient.admitted_date || new Date().toISOString())} days ago
                                      </Badge>
                                      <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive">
                                        Discharge Summary Pending
                                      </Badge>
                                    </div>
                                  </div>
                                </div>
                                <Button
                                  onClick={() => setSelectedOnboardingPatient(patient)}
                                  className="bg-accent hover:bg-accent/90"
                                >
                                  <FileText className="w-4 h-4 mr-2" />
                                  Complete Discharge Summary
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 text-[#14CAF4] mx-auto mb-4" />
                    <h3 className="text-primary mb-2">All Caught Up!</h3>
                    <p className="text-muted-foreground">
                      No pending discharge summaries. All patients have been onboarded successfully.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
